import { formatDisplayDateTime } from "../../utils/dateUtils";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { AxiosError } from "axios";
import {
  Result,
  activityFrequency,
  NamedItem,
} from "../../interface/ActivityFrequency";
import { ColumnDef } from "@tanstack/react-table";
import { Button } from "../../components/ui/button";
import { ActivityFrequencyAxiosInstance } from "../../Network/ActivityFrequencyAxiosInstance";
import { ActivityMedicalAxiosInstance } from "../../Network/Activitymedical";
import { ActivityCustomAxiosInstance } from "../../Network/ActivityCustom";
import { SpecialtyAxiosInstance } from "../../Network/SpecialtyAxiosInstance";
import { PatientRelationAxiosInstance } from "../../Network/PatientRelationAxiosInstance";
import { PatientActivityManagementAxiosInstance } from "../../Network/PatientActivityManagementAxiosInstance";
import { LoginAxiosInstance } from "../../Network/LoginAxiosInstance";
import { Option } from "../../interface/Option";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/Label";
import { Toaster } from "../../components/ui/toaster";
import { useToast } from "../../hooks/use-toast";
import DataTable2 from "shared/DataTable2";
import UnifiedSearchBox from "../../shared/UnifiedSearchBox";
import ServerDeletedPills from "shared/ServerDeletedPills";
import { ServerDeletedFilterValue } from "shared/ServerListFilters";
import ListOfNewNetworkName from "../Shared/NewNetworkName";
import ListOfInsuranceplanName from "../Shared/ListOfInsuranceplanName";
import VipPatientSelector from "Pages/Shared/VipPatient";
import ListOfProviderName from "../Shared/ListOfProviderName";
import PolicyNames from "../Shared/PolicyNames";
import ListofActivityCode from "../Shared/ListOfActivityCode";
import ListOfActivityCustom from "../Shared/ListOfActivityCustom";
import ListOfActivityMedical from "../Shared/ListOfActivityMedical";
import FileUpload from "Pages/uploadFile/ActivityFrequency/ActivityFrequency";
import ListOfDoctorName from "../Shared/Doctor";
import Vip from "../Shared/Vip";
import ListOfSpecialty from "../Shared/Specialty";
import useQaAutomationIds from "../../utils/useQaAutomationIds";

interface PaginationMeta {
  page?: number;
  limit?: number;
  totalPages?: number;
  totalCount?: number;
  allowedLimits?: number[];
}

interface ActivityFrequencyListResponse {
  success?: boolean;
  result?: Result[] | { data?: Result[]; totalCount?: number };
  data?: Result[];
  pagination?: PaginationMeta;
  meta?: PaginationMeta;
  page?: number;
  limit?: number;
  totalPages?: number;
  totalCount?: number;
  allowedLimits?: number[];
}

type FetchItemsOptions = {
  pageIndex?: number;
  pageSize?: number;
};

export interface PolicyItem {
  id?: number;
  Policy_id: string;
  Policy_name: string;
  plans?: Array<{
    Plan_name?: string;
    plan_name?: string;
    name?: string;
    id?: number;
    [key: string]: any;
  }>;
}

type CodeByType = {
  CPT: Option[];
  HCPCS: Option[];
  Dental: Option[];
  Drugs: Option[];
};

const EMPTY_CODE_BY_TYPE: CodeByType = { CPT: [], HCPCS: [], Dental: [], Drugs: [] };

const TYPE_MAP: Record<string, keyof CodeByType> = {
  cpt: "CPT", hcpcs: "HCPCS", dental: "Dental", drugs: "Drugs",
};


const toDisplayText = (value: any, fallback = "—"): string => {
  if (value === null || value === undefined || value === "") return fallback;

  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  if (Array.isArray(value)) {
    const parts = value
      .map((entry) => toDisplayText(entry, ""))
      .filter((entry) => entry.trim().length > 0);
    return parts.length > 0 ? parts.join(", ") : fallback;
  }

  if (typeof value === "object") {
    const candidates = [
      value.name,
      value.label,
      value.Provider_name,
      value.provider_name,
      value.Doctor_name,
      value.doctor_name,
      value.network_name,
      value.Network_name,
      value.Plan_name,
      value.plan_name,
      value.Policy_name,
      value.policy_name,
      value.Patient_name,
      value.patient_name,
      value.Specialty_name,
      value.specialty_name,
      value.vip_name,
      value.Vip_name,
      value.code,
    ];

    for (const candidate of candidates) {
      if (
        candidate !== null &&
        candidate !== undefined &&
        candidate !== "" &&
        typeof candidate !== "object"
      ) {
        return String(candidate);
      }
    }

    if (value.value !== undefined && value.value !== null && value.value !== value) {
      const nested = toDisplayText(value.value, "");
      if (nested) return nested;
    }

    if (value.id !== undefined && value.id !== null) {
      return `ID ${String(value.id)}`;
    }
  }

  return fallback;
};

// An inactive linked entity does not make its parent relation inactive.
const entityInactive = (value: any): boolean => {
  if (!value || typeof value !== "object") return false;
  const status = String(value.status ?? value.Status ?? "").trim().toLowerCase();
  const active = value.is_active ?? value.isActive ?? value.active;
  return value.__inactive === true || value.isInactive === true ||
    active === false || active === 0 || active === "false" || active === "0" ||
    status === "inactive" || status === "disabled";
};
const entitySelectedOption = (value: any, label: string): Option => ({
  ...(value && typeof value === "object" ? value : {}),
  value: getEntityId(value), label,
  is_active: value?.is_active ?? value?.isActive,
  status: value?.status ?? value?.Status,
  __inactive: entityInactive(value),
} as Option);
const groupStyle = (value: any, kind: "medical" | "custom") => {
  if (entityInactive(value)) return { bg: "#e5e7eb", border: "#9ca3af", text: "#334155" };
  return kind === "medical"
    ? { bg: "#f0fdf4", border: "#86efac", text: "#166534" }
    : { bg: "#faf5ff", border: "#d8b4fe", text: "#6b21a8" };
};
const getEntityId = (value: any): string | number => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string" || typeof value === "number") return value;
  if (typeof value === "object") {
    return (
      value.id ??
      value.value ??
      value.Provider_id ??
      value.provider_id ??
      value.Doctor_id ??
      value.doctor_id ??
      value.network_id ??
      value.plan_id ??
      value.specialty_id ??
      value.vip_id ??
      ""
    );
  }
  return "";
};


const getSqlId = (value: any): number | null => {
  if (value === null || value === undefined) return null;

  if (typeof value === "object") {
    const candidate =
      value.dbId ??
      value.db_id ??
      value.id ??
      value.value?.dbId ??
      value.value?.db_id ??
      value.value?.id ??
      value.value;

    if (
      candidate !== null &&
      candidate !== undefined &&
      typeof candidate !== "object"
    ) {
      const id = Number(candidate);
      return Number.isInteger(id) && id > 0 ? id : null;
    }

    return null;
  }

  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? id : null;
};

const toNumericSqlIds = (values: any[] = []): number[] =>
  values
    .map((value) => getSqlId(value))
    .filter((id): id is number => id !== null);

const toStringSqlIds = (values: any[] = []): string[] =>
  toNumericSqlIds(values).map(String);

const getPolicySqlIds = (values: any[] = []): number[] =>
  values
    .map((policy: any) =>
      getSqlId(
        policy?.id ??
          policy?.dbId ??
          policy?.db_id ??
          policy?.value?.id ??
          (typeof policy?.value !== "object" ? policy?.value : null)
      )
    )
    .filter((id): id is number => id !== null);

const firstEntityText = (...values: any[]): string => {
  for (const value of values) {
    if (
      value !== null &&
      value !== undefined &&
      typeof value !== "object"
    ) {
      const str = String(value).trim();
      if (str) return str;
    }
  }
  return "";
};

const formatSqlEntityLabel = (
  item: any,
  ...nameCandidates: any[]
): string => {
  if (item === null || item === undefined) return "";
  if (typeof item !== "object") return String(item);

  const id = getSqlId(item);
  const name = firstEntityText(...nameCandidates);

  if (id !== null) {
    return name ? `(${id}) ${name}` : `(${id})`;
  }

  return name;
};

const formatBusinessEntityLabel = (
  item: any,
  businessIdCandidates: any[],
  ...nameCandidates: any[]
): string => {
  if (item === null || item === undefined) return "";
  if (typeof item !== "object") return String(item);

  const businessId = firstEntityText(...businessIdCandidates);
  const name = firstEntityText(...nameCandidates);

  if (businessId) return name ? `(${businessId}) ${name}` : `(${businessId})`;
  return name;
};

const formatActivityFrequencyPatientLabel = (patient: any): string => {
  if (patient === null || patient === undefined) return "";
  if (typeof patient !== "object") return String(patient);

  const name = firstEntityText(
    patient.full_name,
    patient.Full_name,
    patient.name,
    patient.Patient_name,
    patient.patient_name,
    [patient.First_name ?? patient.first_name, patient.Last_name ?? patient.last_name]
      .filter(Boolean)
      .join(" ")
  );

  const nationalId = firstEntityText(
    patient.National_id,
    patient.national_id
  );
  const memberId = firstEntityText(
    patient.Member_id,
    patient.member_id
  );
  const passport = firstEntityText(
    patient.Patient_passport,
    patient.patient_passport,
    patient.passport
  );

  const identifiers = [
    nationalId ? `National ID: ${nationalId}` : "",
    memberId ? `Member ID: ${memberId}` : "",
    passport ? `Passport: ${passport}` : "",
  ].filter(Boolean);

  return identifiers.length > 0
    ? `${name || "Patient"} (${identifiers.join(" | ")})`
    : name || (getSqlId(patient) !== null ? `Patient ID ${getSqlId(patient)}` : "Patient");
};

const TYPE_COLORS: Array<{
  key: keyof CodeByType;
  bg: string; border: string; text: string; badgeBg: string;
}> = [
  { key: "CPT",    bg: "#eff6ff", border: "#93c5fd", text: "#1e40af", badgeBg: "#dbeafe" },
  { key: "HCPCS",  bg: "#f0fdf4", border: "#86efac", text: "#166534", badgeBg: "#dcfce7" },
  { key: "Dental", bg: "#faf5ff", border: "#d8b4fe", text: "#6b21a8", badgeBg: "#f3e8ff" },
  { key: "Drugs",  bg: "#fff7ed", border: "#fdba74", text: "#9a3412", badgeBg: "#ffedd5" },
];

// â”€â”€ Layout helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const FormSection: React.FC<{ step: number; title: string; children: React.ReactNode }> = ({ step, title, children }) => (
  <div style={{ border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden", background: "#fff" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 18px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
      <span style={{ width: 24, height: 24, borderRadius: "50%", background: "#1e40af", color: "#fff", fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{step}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{title}</span>
    </div>
    <div style={{ padding: "18px" }}>{children}</div>
  </div>
);

const PeriodCard: React.FC<{ title: string; required?: boolean; accent?: boolean; hint: string; children: React.ReactNode }> = ({ title, required, accent, hint, children }) => (
  <div style={{ background: accent ? "#eff6ff" : "#f8fafc", border: `1px solid ${accent ? "#bfdbfe" : "#e2e8f0"}`, borderRadius: 8, padding: "12px 14px" }}>
    <div style={{ fontSize: 11, fontWeight: 600, color: accent ? "#1d4ed8" : "#64748b", marginBottom: 10, textTransform: "uppercase" as const, letterSpacing: "0.04em" }}>
      {title}{required && <span style={{ color: "#ef4444", marginLeft: 2 }}>*</span>}
    </div>
    <div style={{ display: "flex", alignItems: "flex-end", gap: 10 }}>
      {children}
      <div style={{ fontSize: 11, color: accent ? "#3b82f6" : "#94a3b8", paddingBottom: 6, whiteSpace: "nowrap" as const, marginLeft: "auto" }}>{hint}</div>
    </div>
  </div>
);

// â”€â”€ Badge cell for table â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const colorMap: Record<string, { bg: string; border: string; text: string }> = {
  CPT:    { bg: "#eff6ff", border: "#93c5fd", text: "#1e40af" },
  HCPCS:  { bg: "#f0fdf4", border: "#86efac", text: "#166534" },
  Dental: { bg: "#faf5ff", border: "#d8b4fe", text: "#6b21a8" },
  Drugs:  { bg: "#fff7ed", border: "#fdba74", text: "#9a3412" },
};

// â”€â”€ EntityCard â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const entityColors: Record<string, { border: string; bg: string; text: string; badgeBg: string }> = {
  blue:    { border: "border-blue-200",    bg: "bg-blue-50",    text: "text-blue-700",    badgeBg: "bg-blue-50 text-blue-700 border-blue-200" },
  cyan:    { border: "border-cyan-200",    bg: "bg-cyan-50",    text: "text-cyan-700",    badgeBg: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  purple:  { border: "border-purple-200",  bg: "bg-purple-50",  text: "text-purple-700",  badgeBg: "bg-purple-50 text-purple-700 border-purple-200" },
  amber:   { border: "border-amber-200",   bg: "bg-amber-50",   text: "text-amber-700",   badgeBg: "bg-amber-50 text-amber-700 border-amber-200" },
  emerald: { border: "border-emerald-200", bg: "bg-emerald-50", text: "text-emerald-700", badgeBg: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  pink:    { border: "border-pink-200",    bg: "bg-pink-50",    text: "text-pink-700",    badgeBg: "bg-pink-50 text-pink-700 border-pink-200" },
  yellow:  { border: "border-yellow-200",  bg: "bg-yellow-50",  text: "text-yellow-700",  badgeBg: "bg-yellow-50 text-yellow-700 border-yellow-200" },
  indigo:  { border: "border-indigo-200",  bg: "bg-indigo-50",  text: "text-indigo-700",  badgeBg: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  slate:   { border: "border-slate-200",   bg: "bg-slate-50",   text: "text-slate-700",   badgeBg: "bg-slate-50 text-slate-700 border-slate-200" },
  teal:    { border: "border-teal-200",    bg: "bg-teal-50",    text: "text-teal-700",    badgeBg: "bg-teal-50 text-teal-700 border-teal-200" },
};

interface EntityCardProps {
  label: string;
  icon: string;
  color: string;
  isActive: boolean;
  mode: "EXCLUSION" | "INCLUSION" | null;
  onExclusion: () => void;
  onInclusion: () => void;
  onClear: () => void;
  children: React.ReactNode;
}

const EntityCard: React.FC<EntityCardProps> = ({
  label, icon, color, isActive, mode, onExclusion, onInclusion, onClear, children,
}) => {
  const c = entityColors[color] ?? entityColors.slate;

  const preserveScroll = (fn: () => void) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const dialog = (e.currentTarget as HTMLElement).closest('[role="dialog"]');
    const scrollable = dialog?.querySelector('.overflow-y-auto') as HTMLElement | null;
    const scrollTop = scrollable?.scrollTop ?? 0;
    fn();
    requestAnimationFrame(() => {
      if (scrollable) scrollable.scrollTop = scrollTop;
    });
  };

  return (
    <div className={`bg-white rounded-xl border p-4 transition-all relative ${isActive ? `${c.border} shadow-sm` : "border-slate-100"}`}>
      {isActive && (
        <button
          type="button"
          onClick={preserveScroll(onClear)}
          className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-slate-50 text-slate-400 hover:bg-red-50 hover:text-red-500 transition-all border border-slate-100 shadow-sm z-10"
          title="Clear"
        >
          <i className="fa-solid fa-xmark text-[14px]"></i>
        </button>
      )}
      <div className="flex flex-col items-center gap-4">
        <span className={`px-4 py-1 rounded-full text-[10px] font-black tracking-tighter border flex items-center gap-2 ${isActive ? c.badgeBg : "bg-slate-50 text-slate-400 border-slate-200"}`}>
          <i className={`fa-solid ${icon} text-[12px]`}></i> {label}
        </span>
        <div className="flex bg-slate-100/80 rounded-lg p-1 border border-slate-200 w-full shadow-inner">
          <button
            type="button"
            onClick={preserveScroll(onExclusion)}
            className={`flex-1 py-2 text-[11px] font-bold rounded-md transition-all ${isActive && mode === "EXCLUSION" ? "bg-red-600 text-white shadow-md" : "text-slate-500 hover:bg-slate-200"}`}
          >
            Exclusion
          </button>
          <button
            type="button"
            onClick={preserveScroll(onInclusion)}
            className={`flex-1 py-2 text-[11px] font-bold rounded-md transition-all ${isActive && mode === "INCLUSION" ? "bg-emerald-600 text-white shadow-md" : "text-slate-500 hover:bg-slate-200"}`}
          >
            Inclusion
          </button>
        </div>
      </div>
      {isActive && (
        <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
          {children}
        </div>
      )}
    </div>
  );
};

// â”€â”€ preserveScrollCheckbox helper â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const preserveScrollCheckbox = (e: React.ChangeEvent<HTMLInputElement>, fn: () => void) => {
  const el = e.currentTarget as HTMLElement;
  const dialog = el.closest('[role="dialog"]');
  const scrollable = dialog?.querySelector('.overflow-y-auto') as HTMLElement | null;
  const scrollTop = scrollable?.scrollTop ?? 0;
  fn();
  requestAnimationFrame(() => {
    if (scrollable) scrollable.scrollTop = scrollTop;
  });
};

// â”€â”€ mode conversion helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const cardToMode = (mode: "EXCLUSION" | "INCLUSION" | null): string => {
  if (mode === "EXCLUSION") return "EXCLUDED";
  if (mode === "INCLUSION") return "INCLUDED";
  return "DISABLED";
};

const modeToCard = (mode: string | undefined): "EXCLUSION" | "INCLUSION" | null => {
  if (mode === "EXCLUDED") return "EXCLUSION";
  if (mode === "INCLUDED") return "INCLUSION";
  return null;
};

// â”€â”€ getUserRole â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const getUserRole = (): string => {
  try {
    const token = localStorage.getItem("token") || "";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.userType || "";
  } catch { return ""; }
};

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const ActivityFrequency: React.FC = () => {
  useQaAutomationIds("activity-frequency");
  const [items, setItems] = useState<activityFrequency>({ result: [] });
  const [relationName, setRelationName] = useState<string>("");
  const [selectedMainOption, setSelectedMainOption] = useState<string | null>(null);
  const [activityCodeByType, setActivityCodeByType] = useState<CodeByType>({ ...EMPTY_CODE_BY_TYPE });
  const [customActivityGroups, setCustomActivityGroups] = useState<Option[]>([]);
  const [medicalActivityGroups, setMedicalActivityGroups] = useState<Option[]>([]);
  const [frequencyType, setFrequencyType] = useState<string>("daily");
  const [frequencyLimit, setFrequencyLimit] = useState<number>(0);
  const [recurringLimit, setRecurringLimit] = useState<number>(0);
  const [interval, setInterval] = useState<number>(0);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [editIndex, setEditIndex] = useState<null | number>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showAllUpdates, setShowAllUpdates] = useState(false);

  // â”€â”€ Entities toggle â”€â”€
  const [showEntities, setShowEntities] = useState(false);

  // â”€â”€ Provider â”€â”€
  const [provider, setProvider] = useState<Option[]>([]);
  const [providerMode, setProviderMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isProviderActive, setIsProviderActive] = useState(false);
  const [selectAllProviders, setSelectAllProviders] = useState(false);
  const [clearProvider, setClearProvider] = useState(false);

  // â”€â”€ Doctor â”€â”€
  const [doctor, setDoctor] = useState<Option[]>([]);
  const [doctorMode, setDoctorMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isDoctorActive, setIsDoctorActive] = useState(false);
  const [selectAllDoctor, setSelectAllDoctor] = useState(false);
  const [clearDoctor, setClearDoctor] = useState(false);

  // â”€â”€ Networks â”€â”€
  const [networks, setNetworks] = useState<Option[]>([]);
  const [networkMode, setNetworkMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isNetworkActive, setIsNetworkActive] = useState(false);
  const [selectAllNetworks, setSelectAllNetworks] = useState(false);
  const [clearNetworks, setClearNetworks] = useState(false);

  // â”€â”€ Insurance Plan â”€â”€
  const [insuranceplan, setInsuranceplan] = useState<Option[]>([]);
  const [insuranceMode, setInsuranceMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isInsuranceActive, setIsInsuranceActive] = useState(false);
  const [selectAllInsurance, setSelectAllInsurance] = useState(false);
  const [clearInsuranceplan, setClearInsuranceplan] = useState(false);

  // â”€â”€ Payer â”€â”€
  const [Payer, setPayer] = useState<string[]>([]);
  const [payerMode, setPayerMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isPayerActive, setIsPayerActive] = useState(false);
  const [selectAllPayer, setSelectAllPayer] = useState(false);

  // â”€â”€ Policy â”€â”€
  const [policy, setPolicy] = useState<PolicyItem[]>([]);
  const [policyMode, setPolicyMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isPolicyActive, setIsPolicyActive] = useState(false);
  const [selectAllPolicy, setSelectAllPolicy] = useState(false);
  const [clearPolicy, setClearPolicy] = useState(false);

  // â”€â”€ Patients â”€â”€
  const [patients, setPatients] = useState<Option[]>([]);
  const [patientMode, setPatientMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isPatientActive, setIsPatientActive] = useState(false);
  const [selectAllPatients, setSelectAllPatients] = useState(false);
  const [clearPatient, setClearPatient] = useState(false);

  // â”€â”€ VIP â”€â”€
  const [vip, setVip] = useState<Option[]>([]);
  const [vipMode, setVipMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isVipActive, setIsVipActive] = useState(false);
  const [selectAllVip, setSelectAllVip] = useState(false);
  const [clearVip, setClearVip] = useState(false);

  // â”€â”€ User Level â”€â”€
  const [userLevel, setUserLevel] = useState<string[]>([]);
  const [userMode, setUserMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isUserActive, setIsUserActive] = useState(false);
  const [selectAllUser, setSelectAllUser] = useState(false);

  // â”€â”€ Specialty â”€â”€
  const [specialty, setSpecialty] = useState<Option[]>([]);
  const [specialtyMode, setSpecialtyMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isSpecialtyActive, setIsSpecialtyActive] = useState(false);
  const [selectAllSpecialty, setSelectAllSpecialty] = useState(false);
  const [clearSpecialty, setClearSpecialty] = useState(false);

  // â”€â”€ clear flags for activity â”€â”€
  const [clearCustomActivityGroups, setClearCustomActivityGroups] = useState(false);
  const [clearMedicalActivityGroups, setClearMedicalActivityGroups] = useState(false);

  // â”€â”€ filters â”€â”€
  const [filterStatus, setFilterStatus] = useState<string>("");
  const [deletedFilter, setDeletedFilter] = useState<ServerDeletedFilterValue>("false");
  const [filterSearch, setFilterSearch] = useState<string>("");
  const [debouncedFilterStatus, setDebouncedFilterStatus] = useState<string>("");
  const [debouncedFilterSearch, setDebouncedFilterSearch] = useState<string>("");

  // â”€â”€ view dialog â”€â”€
  const [selectedItem, setSelectedItem] = useState<Result | null>(null);
  const [allCodesDialogOpen, setAllCodesDialogOpen] = useState(false);
  const [allCodesDialogItems, setAllCodesDialogItems] = useState<{ code: any; typeKey: string }[]>([]);
  const [allCodesDialogRow, setAllCodesDialogRow] = useState<Result | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [usersMap, setUsersMap] = useState<Record<number, string>>({});

  // â”€â”€ Backend pagination â”€â”€
  // Frontend pageIndex is zero-based; Backend page is one-based.
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState<number | undefined>(undefined);
  const [allowedPageSizes, setAllowedPageSizes] = useState<number[]>([5, 10, 30, 50, 100]);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const fetchRequestIdRef = useRef(0);

  // â”€â”€ Force / Delete dialog â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const [forceDialogOpen, setForceDialogOpen] = useState(false);
  const [forceId, setForceId] = useState<string | number | null>(null);
  const [forceError, setForceError] = useState<string>("");
  const [forceAction, setForceAction] = useState<"softDelete" | "hardDelete" | "status" | null>(null);

  const timestamp = new Date().toISOString();
  const token = localStorage.getItem("token") || "";
  const { toast } = useToast();

  // â”€â”€ Permissions â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const userRole = getUserRole();
  const canManageDeleted = userRole === "system_admin" || userRole === "super_super_admin";

  // â”€â”€ Reset all entity states â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const resetAllEntityStates = () => {
    setProvider([]); setProviderMode(null); setIsProviderActive(false); setSelectAllProviders(false);
    setDoctor([]); setDoctorMode(null); setIsDoctorActive(false); setSelectAllDoctor(false);
    setNetworks([]); setNetworkMode(null); setIsNetworkActive(false); setSelectAllNetworks(false);
    setInsuranceplan([]); setInsuranceMode(null); setIsInsuranceActive(false); setSelectAllInsurance(false);
    setPayer([]); setPayerMode(null); setIsPayerActive(false); setSelectAllPayer(false);
    setPolicy([]); setPolicyMode(null); setIsPolicyActive(false); setSelectAllPolicy(false);
    setPatients([]); setPatientMode(null); setIsPatientActive(false); setSelectAllPatients(false);
    setVip([]); setVipMode(null); setIsVipActive(false); setSelectAllVip(false);
    setUserLevel([]); setUserMode(null); setIsUserActive(false); setSelectAllUser(false);
    setSpecialty([]); setSpecialtyMode(null); setIsSpecialtyActive(false); setSelectAllSpecialty(false);
    setClearProvider(true); setClearDoctor(true); setClearNetworks(true);
    setClearInsuranceplan(true); setClearPatient(true); setClearPolicy(true);
    setClearVip(true); setClearSpecialty(true);
  };

  const handleToggleEntities = () => {
    if (showEntities) resetAllEntityStates();
    setShowEntities((prev) => !prev);
  };

  const validateEntities = (): string | null => {
    const checks = [
      { isActive: isProviderActive,   hasData: selectAllProviders || provider.length > 0,     label: "Providers" },
      { isActive: isDoctorActive,     hasData: selectAllDoctor    || doctor.length > 0,        label: "Doctor" },
      { isActive: isNetworkActive,    hasData: selectAllNetworks  || networks.length > 0,      label: "Networks" },
      { isActive: isInsuranceActive,  hasData: selectAllInsurance || insuranceplan.length > 0, label: "Insurance Plan" },
      { isActive: isPolicyActive,     hasData: selectAllPolicy    || policy.length > 0,        label: "Policy" },
      { isActive: isPatientActive,    hasData: selectAllPatients  || patients.length > 0,      label: "Patients" },
      { isActive: isVipActive,        hasData: selectAllVip       || vip.length > 0,           label: "VIP" },
      { isActive: isSpecialtyActive,  hasData: selectAllSpecialty || specialty.length > 0,     label: "Specialty" },
      { isActive: isPayerActive,      hasData: selectAllPayer     || Payer.length > 0,         label: "Payer" },
      { isActive: isUserActive,       hasData: selectAllUser      || userLevel.length > 0,     label: "User Level" },
    ];
    const empty = checks.filter(c => c.isActive && !c.hasData).map(c => c.label);
    if (empty.length >= 1) {
      return `The following cards are open but have no data selected: ${empty.join(", ")}. Please select at least one item for each, or close them using the âœ• button.`;
    }
    return null;
  };

  const clear = () => {
    setRelationName(""); setSelectedMainOption(null);
    setActivityCodeByType({ ...EMPTY_CODE_BY_TYPE });
    setCustomActivityGroups([]); setMedicalActivityGroups([]);
    setFrequencyType("daily"); setFrequencyLimit(0); setRecurringLimit(0); setInterval(0);
    setIsActive(true);
    setShowEntities(false);
    resetAllEntityStates();
  };

  const buildActivityPayload = () => {
    const allWithTypes: { code: any; type: string }[] = [];
    (Object.keys(activityCodeByType) as (keyof CodeByType)[]).forEach((typeKey) => {
      activityCodeByType[typeKey].forEach((opt) => {
        allWithTypes.push({ code: opt.value, type: typeKey.toLowerCase() });
      });
    });
    return {
      activity_code: allWithTypes.map((a) => a.code),
      activity_type: allWithTypes.map((a) => a.type),
      types: Array.from(new Set(allWithTypes.map((a) => a.type))),
    };
  };

  const fetchItems = useCallback(
    async (options: FetchItemsOptions = {}): Promise<void> => {
      const search = debouncedFilterSearch.trim() || undefined;
      const requestedPageIndex = options.pageIndex ?? currentPage;
      const requestedPageSize = options.pageSize ?? pageSize;
      const requestId = ++fetchRequestIdRef.current;

      try {
        setIsTableLoading(true);

        const response =
          await ActivityFrequencyAxiosInstance.get<ActivityFrequencyListResponse>(
            "getAllActivityFrequency",
            {
              params: {
                page: requestedPageIndex + 1,
                limit: requestedPageSize,
                name: search,
                activity: search,
                disease: search,
                is_active:
                  debouncedFilterStatus === "true" ||
                  debouncedFilterStatus === "false"
                    ? debouncedFilterStatus
                    : undefined,
                is_deleted: deletedFilter,
              },
            }
          );

        if (requestId !== fetchRequestIdRef.current) return;

        const payload: any = response.data;
        const resultValue = payload?.result;

        const rows: Result[] = Array.isArray(resultValue)
          ? resultValue
          : Array.isArray(resultValue?.data)
            ? resultValue.data
            : Array.isArray(payload?.data)
              ? payload.data
              : [];

        const pagination: PaginationMeta =
          payload?.pagination ?? payload?.meta ?? payload ?? {};

        const rawTotalCount =
          pagination.totalCount ??
          payload?.totalCount ??
          (!Array.isArray(resultValue)
            ? resultValue?.totalCount
            : undefined);

        const totalCountValue = Number(rawTotalCount);

        const totalPagesValue = Number(
          pagination.totalPages ??
            payload?.totalPages ??
            (Number.isFinite(totalCountValue)
              ? Math.ceil(totalCountValue / requestedPageSize)
              : 1)
        );

        const nextTotalPages = Math.max(
          1,
          Number.isFinite(totalPagesValue) ? totalPagesValue : 1
        );

        const backendAllowedLimits =
          pagination.allowedLimits ?? payload?.allowedLimits;

        if (
          Array.isArray(backendAllowedLimits) &&
          backendAllowedLimits.length > 0
        ) {
          const cleanLimits = backendAllowedLimits
            .map(Number)
            .filter(
              (value: number) =>
                Number.isFinite(value) && value > 0
            );

          if (cleanLimits.length > 0) {
            setAllowedPageSizes(cleanLimits);
          }
        }

        setTotalCount(
          Number.isFinite(totalCountValue)
            ? totalCountValue
            : undefined
        );

        setTotalPages(nextTotalPages);

        setItems({
          result: [...rows]
            .sort(
              (a: any, b: any) =>
                Number(b.id) - Number(a.id)
            )
            .sort((a: any, b: any) =>
              a.is_deleted === b.is_deleted
                ? 0
                : a.is_deleted
                  ? 1
                  : -1
            ),
        });

        if (requestedPageIndex >= nextTotalPages) {
          setCurrentPage(nextTotalPages - 1);
        }
      } catch (error) {
        if (requestId === fetchRequestIdRef.current) {
          console.error(
            "Failed to fetch activity frequency:",
            error
          );

          toast({
            title: "Error",
            description: "Failed to fetch activity frequency",
            variant: "destructive",
          });
        }
      } finally {
        if (requestId === fetchRequestIdRef.current) {
          setIsTableLoading(false);
        }
      }
    },
    [
      currentPage,
      pageSize,
      debouncedFilterSearch,
      debouncedFilterStatus,
      deletedFilter,
      toast,
    ]
  );

  useEffect(() => {
    fetchUsers();
  }, []);

  // Debounce only the filters. Page navigation and page-size changes remain immediate.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setCurrentPage(0);
      setDebouncedFilterSearch(filterSearch);
      setDebouncedFilterStatus(filterStatus);
    }, 350);

    return () => window.clearTimeout(timer);
  }, [filterSearch, filterStatus]);

  useEffect(() => {
    void fetchItems();
  }, [fetchItems]);

  const fetchUsers = async () => {
    try {
      const res = await LoginAxiosInstance.get("/");
      const raw: any[] = res.data?.users ?? res.data?.user ?? res.data ?? [];
      const map: Record<number, string> = {};
      raw.forEach((u: any) => {
        if (u.id != null) map[Number(u.id)] = u.name ?? u.username ?? String(u.id);
      });
      setUsersMap(map);
    } catch {}
  };

  const resolveUserName = (val: any): string => {
    if (val == null) return "";
    if (typeof val === "object") return val.name ?? val.username ?? "";
    if (typeof val === "string" && isNaN(Number(val))) return val;
    return usersMap[Number(val)] ?? String(val);
  };

  const formatDate = (dateStr?: string): string => {
    return formatDisplayDateTime(dateStr, "");
  };
  // â”€â”€ Delete (was handleDeleteClick)  now soft delete â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const handleSoftDelete = async (id: string | number): Promise<void> => {
    try {
      const response = await ActivityFrequencyAxiosInstance.delete(
        `softDeleteactivityFrequency/${id}`,
        { data: { token } }
      );
      if (response.data.success) {
        await fetchItems();
        toast({ title: "Success", description: "Activity frequency Deleted Successfully." });
      } else {
        setForceId(id);
        setForceAction("softDelete");
        setForceError(response.data.error || "This record is used elsewhere.");
        setForceDialogOpen(true);
      }
    } catch (error: any) {
      const errMsg = error.response?.data?.error || "Failed to delete activity frequency";
      if (error.response?.status === 409 || errMsg.toLowerCase().includes("used")) {
        setForceId(id); setForceAction("softDelete"); setForceError(errMsg); setForceDialogOpen(true);
      } else {
        toast({ title: "Error", description: errMsg, variant: "destructive" });
      }
    }
  };

  // â”€â”€ Restore â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const restoreItem = async (id: string | number): Promise<void> => {
    try {
      const response = await ActivityFrequencyAxiosInstance.patch(
        `restoreactivityfrequency/${id}`,
        { token }
      );
      if (response.data.success) {
        await fetchItems();
        toast({ title: "Success", description: "Activity frequency restored successfully." });
      } else {
        toast({ title: "Error", description: response.data.error || "Failed to restore", variant: "destructive" });
      }
    } catch (error: any) {
      toast({ title: "Error", description: error.response?.data?.error || "Failed to restore", variant: "destructive" });
    }
  };

  // â”€â”€ Hard Delete (permanent) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const permanentDeleteItem = async (id: string | number, force = false): Promise<void> => {
    try {
      const url = force
        ? `deleteactivityfrequency/${id}?force=true`
        : `deleteactivityfrequency/${id}`;
      const response = await ActivityFrequencyAxiosInstance.delete(url, { data: { token } });
      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null);
        toast({ title: "Success", description: "Activity frequency Permanently Deleted." });
      } else {
        setForceAction("hardDelete");
        setForceError(response.data.error || "This record is used elsewhere.");
      }
    } catch (error: any) {
      setForceAction("hardDelete");
      setForceError(error.response?.data?.error || "Failed to delete activity frequency");
    }
  };

  // â”€â”€ Force Soft Delete â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const forceSoftDelete = async (id: string | number): Promise<void> => {
    try {
      const response = await ActivityFrequencyAxiosInstance.delete(
        `softDeleteactivityFrequency/${id}?force=true`,
        { data: { token } }
      );
      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null);
        toast({ title: "Success", description: "Activity frequency force Deleted Successfully." });
      } else {
        setForceError(response.data.error || "Failed to force delete");
      }
    } catch (error: any) {
      setForceError(error.response?.data?.error || "Failed to force delete");
    }
  };

  // â”€â”€ Toggle Active Status â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const toggleActiveStatus = async (item: Result, force = false) => {
    try {
      const networkArr = (item as any).network || item.networks || [];
      const patientArr = (item as any).patient || item.patients || [];
      const url = force ? `updateactivityfrequency?force=true` : `updateactivityfrequency`;
      const payload = {
        relation_name: item.relation_name,
        is_active: !item.is_active,
        activity_type: (item as any).activity_type || [],
        activity_code: item.activity_code || [],
        types: (item as any).types || [],
        custom_groups: (item.custom_groups || []).map((g: any) => g.id ?? g),
        medical_groups: (item.medical_groups || []).map((g: any) => g.id ?? g),
        period_unit: item.period_unit || item.frequency_type || "",
        quantity: item.quantity ?? item.frequency_limit ?? 0,
        recurring_limit: item.recurring_limit || 0,
        period_frq: item.period_frq ?? item.interval ?? 0,
        provider: toStringSqlIds(item.provider || []),
        provider_mode: (item as any).provider_mode ?? "DISABLED",
        provider_all: (item as any).provider_all ?? false,
        network: toNumericSqlIds(networkArr),
        network_mode: (item as any).network_mode ?? "DISABLED",
        network_all: (item as any).network_all ?? false,
        insuranceplan: toNumericSqlIds(item.insuranceplan || []),
        insuranceplan_mode: (item as any).insuranceplan_mode ?? "DISABLED",
        insuranceplan_all: (item as any).insuranceplan_all ?? false,
        payer: item.payer || [],
        payer_mode: (item as any).payer_mode ?? "DISABLED",
        payer_all: (item as any).payer_all ?? false,
        patient: toNumericSqlIds(patientArr),
        patient_mode: (item as any).patient_mode ?? "DISABLED",
        patient_all: (item as any).patient_all ?? false,
        policy: getPolicySqlIds(item.policy || []),
        policy_mode: (item as any).policy_mode ?? "DISABLED",
        policy_all: (item as any).policy_all ?? false,
        user_level: item.user_level || [],
        user_mode: (item as any).user_mode ?? "DISABLED",
        user_all: (item as any).user_all ?? false,
        vip: toNumericSqlIds(item.vip || []),
        vip_mode: (item as any).vip_mode ?? "DISABLED",
        vip_all: (item as any).vip_all ?? false,
        doctor: toStringSqlIds(item.doctor || []),
        doctor_mode: (item as any).doctor_mode ?? "DISABLED",
        doctor_all: (item as any).doctor_all ?? false,
        specialty: toNumericSqlIds(item.specialty || []),
        specialty_mode: (item as any).specialty_mode ?? "DISABLED",
        specialty_all: (item as any).specialty_all ?? false,
        id: item.id,
        timestamp: new Date().toISOString(),
        token,
      };
      const response = await ActivityFrequencyAxiosInstance.put(url, payload);
      if (response.data.success) {
        setItems((prev) => {
          const newResult = [...prev.result];
          const idx = newResult.findIndex((i) => i.id === item.id);
          if (idx !== -1) newResult[idx] = { ...newResult[idx], is_active: !item.is_active };
          return { result: newResult };
        });
        setForceDialogOpen(false); setForceError("");
        toast({ title: "Success", description: `Status changed to ${!item.is_active ? "Active" : "Inactive"}.` });
      } else {
        setForceId(item.id);
        setForceAction("status");
        setForceError(response.data.error || "This record is used elsewhere.");
        setForceDialogOpen(true);
      }
    } catch (error: any) {
      const errMsg = error.response?.data?.error || "An unexpected error occurred.";
      if (error.response?.status === 409 || errMsg.toLowerCase().includes("used")) {
        setForceId(item.id);
        setForceAction("status");
        setForceError(errMsg);
        setForceDialogOpen(true);
      } else {
        toast({ title: "Error", description: errMsg, variant: "destructive" });
      }
    }
  };

  // â”€â”€ buildPayload â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const buildPayload = () => {
    const { activity_code, activity_type, types } = buildActivityPayload();
    return {
      relation_name: relationName,
      activity_type, activity_code, types,
      custom_groups: customActivityGroups.map((o) => o.value),
      medical_groups: medicalActivityGroups.map((o) => o.value),
      timestamp,
      period_unit: frequencyType,
      quantity: frequencyLimit,
      recurring_limit: recurringLimit,
      period_frq: interval,
      provider: toStringSqlIds(provider),
      provider_mode: cardToMode(providerMode),
      provider_all: selectAllProviders,
      network: toNumericSqlIds(networks),
      network_mode: cardToMode(networkMode),
      network_all: selectAllNetworks,
      insuranceplan: toNumericSqlIds(insuranceplan),
      insuranceplan_mode: cardToMode(insuranceMode),
      insuranceplan_all: selectAllInsurance,
      payer: Payer,
      payer_mode: cardToMode(payerMode),
      payer_all: selectAllPayer,
      patient: toNumericSqlIds(patients),
      patient_mode: cardToMode(patientMode),
      patient_all: selectAllPatients,
      policy: getPolicySqlIds(policy),
      policy_mode: cardToMode(policyMode),
      policy_all: selectAllPolicy,
      user_level: userLevel,
      user_mode: cardToMode(userMode),
      user_all: selectAllUser,
      vip: toNumericSqlIds(vip),
      vip_mode: cardToMode(vipMode),
      vip_all: selectAllVip,
doctor: toStringSqlIds(doctor),
      doctor_mode: cardToMode(doctorMode),
      doctor_all: selectAllDoctor,
      specialty: toNumericSqlIds(specialty),
      specialty_mode: cardToMode(specialtyMode),
      specialty_all: selectAllSpecialty,
      is_active: isActive,
      token,
    };
  };

  const validateForm = (isUpdate = false): string | null => {
    const actionText = isUpdate ? "to update this relation." : "to add this relation.";
    if (!relationName.trim()) {
      return `Relation Name is required ${actionText}`;
    }
    const hasType =
      selectedMainOption ||
      Object.keys(activityCodeByType).some(
        (key) => activityCodeByType[key as keyof CodeByType].length > 0,
      );
    if (!hasType) {
      return `Activity Type is required ${actionText}`;
    }
    const totalCodes = Object.values(activityCodeByType).reduce(
      (acc, arr) => acc + arr.length,
      0,
    );
    if (
      totalCodes === 0 &&
      customActivityGroups.length === 0 &&
      medicalActivityGroups.length === 0
    ) {
      return `At least one active Activity Code or group is required ${actionText}`;
    }
    if (frequencyLimit === undefined || frequencyLimit === null || frequencyLimit <= 0) {
      return `Quantity is required ${actionText}`;
    }
    if (!frequencyType || !String(frequencyType).trim()) {
      return `Period Unit is required ${actionText}`;
    }
    if (!interval || interval <= 0) {
      return `Period Frequency is required ${actionText}`;
    }
    if (!recurringLimit || recurringLimit <= 0) {
      return `Recurring Limitation is required ${actionText}`;
    }
    if (isActive === undefined || isActive === null) {
      return `Status is required ${actionText}`;
    }
    return null;
  };

  const addItem = async (): Promise<void> => {
    const formError = validateForm(false);
    if (formError) {
      toast({ title: "Error", description: formError, variant: "destructive" });
      return;
    }
    if (relationName.trim().length < 3) { toast({ title: "Error", description: "Relation name must be at least 3 characters.", variant: "destructive" }); return; }
    const entityError = validateEntities();
    if (entityError) { toast({ title: "Validation Error", description: entityError, variant: "destructive" }); return; }
    try {
      const response = await ActivityFrequencyAxiosInstance.post("addactivityfrequency", buildPayload());
      if (response.data.success) {
        setCurrentPage(0);
        await fetchItems({ pageIndex: 0 });
        clear(); setIsDialogOpen(false);
        toast({ title: "Success", description: "New activity frequency group Added Successfully." });
      } else {
        const err = response.data.errors?.[0]?.error || response.data.error || "Failed to add the group.";
        toast({ title: "Error", description: err, variant: "destructive" });
      }
    } catch (error: AxiosError | any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  const updateItem = async (id: string | number): Promise<void> => {
    const formError = validateForm(true);
    if (formError) {
      toast({ title: "Error", description: formError, variant: "destructive" });
      return;
    }
    if (relationName.trim().length < 3) { toast({ title: "Error", description: "Relation name must be at least 3 characters.", variant: "destructive" }); return; }
    const entityError = validateEntities();
    if (entityError) { toast({ title: "Validation Error", description: entityError, variant: "destructive" }); return; }
    try {
      const response = await ActivityFrequencyAxiosInstance.put(`updateactivityfrequency`, { ...buildPayload(), id });
      if (response.data.success) {
        setCurrentPage(0);
        await fetchItems({ pageIndex: 0 });
        clear(); setIsDialogOpen(false);
        toast({ title: "Success", description: "Activity frequency group updated successfully." });
      } else {
        const err = response.data.error || response.data.message || "Failed to update the group.";
        toast({ title: "Error", description: err, variant: "destructive" });
      }
    } catch (error: AxiosError | any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  // â”€â”€ handleEdit â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const handleEdit = async (item: Result) => {
    setClearProvider(false);
    setClearDoctor(false);
    setClearNetworks(false);
    setClearInsuranceplan(false);
    setClearPatient(false);
    setClearPolicy(false);
    setClearVip(false);
    setClearSpecialty(false);

    try {
      const response = await ActivityFrequencyAxiosInstance.get(`getactivityfrequency/${item.id}`);
      const rawEdit: any = response.data.result;
      const u: any = await refreshScopeRecord(rawEdit);

      const grouped: CodeByType = { CPT: [], HCPCS: [], Dental: [], Drugs: [] };
      if (u.activity_code?.length) {
        u.activity_code.forEach((code: any, idx: number) => {
          let rawType = "cpt";
          if (u.activity_types?.[idx]?.type) {
            rawType = u.activity_types[idx].type.toLowerCase();
          } else if (u.activity_type?.[idx]) {
            rawType = typeof u.activity_type[idx] === "string" ? u.activity_type[idx].toLowerCase() : "cpt";
          }
          const mappedKey = TYPE_MAP[rawType] ?? "CPT";
          grouped[mappedKey].push({ value: code, label: code.toString() });
        });
      }
      setActivityCodeByType(grouped);

      if (u.activity_code?.length > 0) setSelectedMainOption("list_of_Activity_code");
      else if (u.medical_groups?.length > 0) setSelectedMainOption("medical_Activity_groups");
      else if (u.custom_groups?.length > 0) setSelectedMainOption("custom_Activity_groups");
      else setSelectedMainOption(null);

      setFrequencyType(u.period_unit || u.frequency_type || "daily");
      setFrequencyLimit(u.quantity ?? u.frequency_limit ?? 0);
      setRecurringLimit(u.recurring_limit || 0);
      setInterval(u.period_frq ?? u.interval ?? 0);
      setRelationName(u.relation_name);
      setIsActive(u.is_active);
      setEditIndex(u.id);

      setCustomActivityGroups(
        (Array.isArray(u.custom_groups) ? u.custom_groups : []).map((o: any) => ({
          ...entitySelectedOption(o, toDisplayText(o)),
        }))
      );
      setMedicalActivityGroups(
        (Array.isArray(u.medical_groups) ? u.medical_groups : []).map((o: any) => ({
          ...entitySelectedOption(o, toDisplayText(o)),
        }))
      );

      const mappedProviders = (Array.isArray(u.provider) ? u.provider : []).map((o: any) => ({
        ...(o && typeof o === "object" ? o : {}),
        __inactive: entityInactive(o),
        value: o?.id ?? o?.dbId ?? o?.db_id ?? getEntityId(o),
        label:
          typeof o === "object"
            ? formatBusinessEntityLabel(
                o,
                [o?.Provider_id, o?.provider_id],
                o?.Provider_name,
                o?.provider_name,
                o?.providerName,
                o?.name
              )
            : String(o),
        dbId: Number(o?.id ?? o?.dbId ?? o?.db_id ?? 0) || undefined,
        providerId: o?.Provider_id ?? o?.provider_id ?? "",
        providerName:
          o?.Provider_name ??
          o?.provider_name ??
          o?.providerName ??
          o?.name ??
          "",
      }));
      setProvider(mappedProviders);
      const pMode = modeToCard(u.provider_mode);
      setProviderMode(pMode);
      setIsProviderActive(pMode !== null || mappedProviders.length > 0 || u.provider_all);
      setSelectAllProviders(u.provider_all ?? false);

      const mappedDoctors = (Array.isArray(u.doctor) ? u.doctor : []).map((o: any) => ({
        ...(o && typeof o === "object" ? o : {}),
        __inactive: entityInactive(o),
        value: o?.id ?? o?.dbId ?? o?.db_id ?? getEntityId(o),
        label:
          typeof o === "object"
            ? formatBusinessEntityLabel(
                o,
                [o?.Doctor_id, o?.doctor_id],
                o?.Doctor_name,
                o?.doctor_name,
                o?.doctorName,
                o?.name
              )
            : String(o),
        dbId: Number(o?.id ?? o?.dbId ?? o?.db_id ?? 0) || undefined,
        doctorId: o?.Doctor_id ?? o?.doctor_id ?? "",
        Doctor_id: o?.Doctor_id ?? o?.doctor_id ?? "",
        doctorName:
          o?.Doctor_name ??
          o?.doctor_name ??
          o?.doctorName ??
          o?.name ??
          "",
        Doctor_name:
          o?.Doctor_name ??
          o?.doctor_name ??
          o?.doctorName ??
          o?.name ??
          "",
      }));
      setDoctor(mappedDoctors);
      const dMode = modeToCard(u.doctor_mode);
      setDoctorMode(dMode);
      setIsDoctorActive(dMode !== null || mappedDoctors.length > 0 || u.doctor_all);
      setSelectAllDoctor(u.doctor_all ?? false);

      const networkSource = Array.isArray(u.network) ? u.network : (Array.isArray(u.networks) ? u.networks : []);
      const mappedNetworks = networkSource.map((o: any) => ({
        ...(o && typeof o === "object" ? o : {}),
        __inactive: entityInactive(o),
        value: o?.id ?? o?.dbId ?? o?.db_id ?? getEntityId(o),
        label:
          typeof o === "object"
            ? formatSqlEntityLabel(
                o,
                o?.Network_name,
                o?.network_name,
                o?.name
              )
            : String(o),
      }));
      setNetworks(mappedNetworks);
      const nMode = modeToCard(u.network_mode);
      setNetworkMode(nMode);
      setIsNetworkActive(nMode !== null || mappedNetworks.length > 0 || u.network_all);
      setSelectAllNetworks(u.network_all ?? false);

      const mappedInsurance = (Array.isArray(u.insuranceplan) ? u.insuranceplan : []).map((o: any) => ({
        ...(o && typeof o === "object" ? o : {}),
        __inactive: entityInactive(o),
        value: o?.id ?? o?.dbId ?? o?.db_id ?? getEntityId(o),
        label:
          typeof o === "object"
            ? formatSqlEntityLabel(
                o,
                o?.Plan_name,
                o?.plan_name,
                o?.name
              )
            : String(o),
      }));
      setInsuranceplan(mappedInsurance);
      const iMode = modeToCard(u.insuranceplan_mode);
      setInsuranceMode(iMode);
      setIsInsuranceActive(iMode !== null || mappedInsurance.length > 0 || u.insuranceplan_all);
      setSelectAllInsurance(u.insuranceplan_all ?? false);

      setPayer(u.payer || []);
      const payMode = modeToCard(u.payer_mode);
      setPayerMode(payMode);
      setIsPayerActive(payMode !== null || (u.payer?.length > 0) || u.payer_all);
      setSelectAllPayer(u.payer_all ?? false);

      const mappedPolicy = Array.isArray(u.policy)
        ? u.policy.map((o: any) => ({
            ...(o && typeof o === "object" ? o : {}),
            __inactive: entityInactive(o),
            id: Number(getEntityId(o)) || 0,
            Policy_id: typeof o === "object" ? String(o?.Policy_id ?? "") : String(o ?? ""),
            Policy_name: typeof o === "object" ? String(o?.Policy_name ?? toDisplayText(o, "")) : String(o ?? ""),
            plans: Array.isArray(o?.plans) ? o.plans : [],
          }))
        : [];
      setPolicy(mappedPolicy);
      const polMode = modeToCard(u.policy_mode);
      setPolicyMode(polMode);
      setIsPolicyActive(polMode !== null || mappedPolicy.length > 0 || u.policy_all);
      setSelectAllPolicy(u.policy_all ?? false);

      const patientSource = Array.isArray(u.patient) ? u.patient : (Array.isArray(u.patients) ? u.patients : []);
      const mappedPatients = patientSource.map((o: any) => ({
        ...(o && typeof o === "object" ? o : {}),
        __inactive: entityInactive(o),
        value: o?.id ?? o?.dbId ?? o?.db_id ?? getEntityId(o),
        label:
          typeof o === "object"
            ? formatActivityFrequencyPatientLabel(o)
            : String(o),
        dbId: Number(o?.id ?? o?.dbId ?? o?.db_id ?? 0) || undefined,
        patientName:
          o?.full_name ??
          o?.Full_name ??
          o?.name ??
          "",
        nationalId: o?.National_id ?? o?.national_id ?? "",
        memberId: o?.Member_id ?? o?.member_id ?? "",
        patientPassport:
          o?.Patient_passport ??
          o?.patient_passport ??
          o?.passport ??
          "",
      }));
      setPatients(mappedPatients);
      const patMode = modeToCard(u.patient_mode);
      setPatientMode(patMode);
      setIsPatientActive(patMode !== null || mappedPatients.length > 0 || u.patient_all);
      setSelectAllPatients(u.patient_all ?? false);

      const mappedVip = (Array.isArray(u.vip) ? u.vip : []).map((o: any) => ({
        ...(o && typeof o === "object" ? o : {}),
        __inactive: entityInactive(o),
        value: o?.id ?? o?.dbId ?? o?.db_id ?? getEntityId(o),
        label:
          typeof o === "object"
            ? formatSqlEntityLabel(
                o,
                o?.name,
                o?.vip_name,
                o?.Vip_name
              )
            : String(o),
      }));
      setVip(mappedVip);
      const vMode = modeToCard(u.vip_mode);
      setVipMode(vMode);
      setIsVipActive(vMode !== null || mappedVip.length > 0 || u.vip_all);
      setSelectAllVip(u.vip_all ?? false);

      setUserLevel(u.user_level || []);
      const uMode = modeToCard(u.user_mode);
      setUserMode(uMode);
      setIsUserActive(uMode !== null || (u.user_level?.length > 0) || u.user_all);
      setSelectAllUser(u.user_all ?? false);

      const specialtyData = Array.isArray(u.specialty) ? u.specialty : [];
      const mappedSpecialty = specialtyData.map((o: any) => ({
        ...(o && typeof o === "object" ? o : {}),
        __inactive: entityInactive(o),
        value: o?.id ?? o?.dbId ?? o?.db_id ?? getEntityId(o),
        label:
          typeof o === "object"
            ? formatBusinessEntityLabel(
                o,
                [o?.speciality_code, o?.specialty_code],
                o?.name,
                o?.Specialty_name,
                o?.specialty_name,
                o?.speciality_name
              )
            : String(o),
      }));
      setSpecialty(mappedSpecialty);
      const spMode = modeToCard(u.specialty_mode);
      setSpecialtyMode(spMode);
      setIsSpecialtyActive(spMode !== null || mappedSpecialty.length > 0 || u.specialty_all);
      setSelectAllSpecialty(u.specialty_all ?? false);

      const hasEntities = !!(
        mappedProviders.length > 0 || u.provider_all ||
        mappedDoctors.length > 0   || u.doctor_all ||
        mappedNetworks.length > 0  || u.network_all ||
        mappedInsurance.length > 0 || u.insuranceplan_all ||
        mappedPolicy.length > 0    || u.policy_all ||
        mappedPatients.length > 0  || u.patient_all ||
        mappedVip.length > 0       || u.vip_all ||
        u.payer?.length > 0        || u.payer_all ||
        u.user_level?.length > 0   || u.user_all ||
        mappedSpecialty.length > 0 || u.specialty_all ||
        (u.provider_mode      && u.provider_mode      !== "DISABLED") ||
        (u.doctor_mode        && u.doctor_mode        !== "DISABLED") ||
        (u.network_mode       && u.network_mode       !== "DISABLED") ||
        (u.insuranceplan_mode && u.insuranceplan_mode !== "DISABLED") ||
        (u.payer_mode         && u.payer_mode         !== "DISABLED") ||
        (u.policy_mode        && u.policy_mode        !== "DISABLED") ||
        (u.patient_mode       && u.patient_mode       !== "DISABLED") ||
        (u.vip_mode           && u.vip_mode           !== "DISABLED") ||
        (u.user_mode          && u.user_mode          !== "DISABLED") ||
        (u.specialty_mode     && u.specialty_mode     !== "DISABLED")
      );
      setShowEntities(hasEntities);

    } catch {
      toast({ title: "Error", description: "Failed to load item data for editing.", variant: "destructive" });
    }
  };

  const handleDownload = (item: Result) => {
    const rows = ["relation_name;activity_type;activity_code;type;medical_groups;custom_groups;activity_date;created_at;period_unit;quantity;recurring_limit;period_frq;provider;provider_mode;network;network_mode;insuranceplan;insuranceplan_mode;payer;payer_mode;patient;patient_mode;policy;policy_mode;user_level;user_mode;doctor;doctor_mode;vip;vip_mode;specialty;specialty_mode;is_active"];
    const activityCodes = item.activity_code || [];
    const itemActivityType = (item as any).activity_type || [];
    const medicalGroups = item.medical_groups || [];
    const customGroups = item.custom_groups || [];
    const providers = item.provider || [];
    const networkArr = (item as any).network || item.networks || [];
    const insuranceplanArr = item.insuranceplan || [];
    const patientArr = (item as any).patient || item.patients || [];
    const policyArr = item.policy || [];
    const userLevelArr = item.user_level || [];
    const doctorArr = item.doctor || [];
    const payerArr = item.payer || [];
    const vipArr = item.vip || [];
    const specialtyArr = item.specialty || [];
    const maxLen = Math.max(activityCodes.length, medicalGroups.length, customGroups.length, providers.length, networkArr.length, insuranceplanArr.length, patientArr.length, policyArr.length, userLevelArr.length, doctorArr.length, payerArr.length, vipArr.length, specialtyArr.length);
    for (let index = 0; index < Math.max(1, maxLen); index++) {
      rows.push([
        item.relation_name || "",
        index < itemActivityType.length ? itemActivityType[index] : "",
        index < activityCodes.length ? activityCodes[index] : "",
        index < itemActivityType.length ? itemActivityType[index] : "",
        index < medicalGroups.length ? medicalGroups[index].name || "" : "",
        index < customGroups.length ? customGroups[index].name || "" : "",
        item.activity_date || "", item.created_at || "",
        item.period_unit || item.frequency_type || "",
        item.quantity ?? item.frequency_limit ?? "",
        item.recurring_limit || "", item.period_frq ?? item.interval ?? "",
        index < providers.length ? providers[index].Provider_name || "" : "",
        (item as any).provider_mode ?? "DISABLED",
        index < networkArr.length ? networkArr[index].network_name || "" : "",
        (item as any).network_mode ?? "DISABLED",
        index < insuranceplanArr.length ? insuranceplanArr[index].Plan_name || "" : "",
        (item as any).insuranceplan_mode ?? "DISABLED",
        index < payerArr.length ? payerArr[index] : "",
        (item as any).payer_mode ?? "DISABLED",
        index < patientArr.length ? toDisplayText(patientArr[index], "") : "",
        (item as any).patient_mode ?? "DISABLED",
        index < policyArr.length ? policyArr[index].Policy_name || "" : "",
        (item as any).policy_mode ?? "DISABLED",
        index < userLevelArr.length ? userLevelArr[index] : "",
        (item as any).user_mode ?? "DISABLED",
        index < doctorArr.length ? doctorArr[index].Doctor_name || "" : "",
        (item as any).doctor_mode ?? "DISABLED",
        index < vipArr.length ? vipArr[index].name || "" : "",
        (item as any).vip_mode ?? "DISABLED",
        index < specialtyArr.length ? specialtyArr[index].name || "" : "",
        (item as any).specialty_mode ?? "DISABLED",
        item.is_active ? "TRUE" : "FALSE",
      ].join(";"));
    }
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `ActivityFrequency${item.id}.csv`; a.click();
    window.URL.revokeObjectURL(url);
  };

  // Relation GET may only include IDs/names of linked groups, with stale or
  // missing status. Resolve against the *current* group directories on View.
  const extractGroupRows = (payload: any): any[] => {
    const base = payload?.result ?? payload?.data ?? payload;
    for (const candidate of [base, base?.data, base?.rows, base?.result,
      base?.getActivityMedicalGroup, base?.getActivityCustomGroup,
      base?.getActivityMedicalGroups, base?.getActivityCustomGroups]) {
      if (Array.isArray(candidate)) return candidate;
    }
    return [];
  };

  const groupIdentifier = (group: any): string => {
    if (group === null || group === undefined) return "";
    if (typeof group !== "object") return String(group);
    const id = group.id ?? group.dbId ?? group.db_id ??
      group.medical_group_id ?? group.custom_group_id ??
      (typeof group.value !== "object" ? group.value : group.value?.id);
    return id === null || id === undefined ? "" : String(id);
  };

  const readGroupDirectory = async (kind: "medical" | "custom"): Promise<any[]> => {
    const client = kind === "medical" ? ActivityMedicalAxiosInstance : ActivityCustomAxiosInstance;
    const endpoint = kind === "medical" ? "getActivityMedicalGroup" : "getActivityCustomGroup";
    const collected: any[] = [];
    // Fetch both active and inactive groups; do not rely on a default active filter.
    // Multiple pages are supported to avoid silently losing old linked groups.
    for (let page = 1; page <= 20; page++) {
      const response = await client.get(endpoint, { params: { page, limit: 1000, is_deleted: "false" } });
      const batch = extractGroupRows(response.data);
      collected.push(...batch);
      const meta = response.data?.pagination ?? response.data?.result?.pagination ?? response.data?.meta;
      const pages = Number(meta?.totalPages);
      if ((Number.isFinite(pages) && page >= pages) || batch.length === 0 ||
        (!Number.isFinite(pages) && batch.length < 1000)) break;
    }
    return collected;
  };

  const refreshLinkedGroups = (linked: any[], directory: any[]): any[] => {
    const catalogue = new Map(directory.map(row => [groupIdentifier(row), row]));
    return linked.map(group => {
      const matched = catalogue.get(groupIdentifier(group));
      if (!matched) return group; // Keep linked record if directory is incomplete.
      const original = group && typeof group === "object" ? group : {};
      return {
        ...original,
        ...matched,
        // Keep the relation's ID and original display label if catalogue omits it.
        id: matched.id ?? original.id ?? groupIdentifier(group),
        name: matched.name ?? original.name ?? original.label ?? toDisplayText(group),
        is_active: matched.is_active ?? matched.isActive ?? matched.active ?? original.is_active,
        status: matched.status ?? matched.Status ?? original.status,
      };
    });
  };

  // Refresh selected entity status from its own catalogue. A relation detail may
  // contain an old copy of an entity without its current is_active flag.
  const extractEntityRows = (payload: any): any[] => {
    const root = payload?.result ?? payload?.data ?? payload;
    for (const value of [root, root?.data, root?.rows, root?.patients,
      root?.specialties, root?.result, payload?.patients, payload?.specialties]) {
      if (Array.isArray(value)) return value;
    }
    return [];
  };

  const entityDirectoryKey = (value: any): string => String(
    value?.id ?? value?.dbId ?? value?.db_id ?? value?.patient_id ??
    value?.specialty_id ?? value?.Specialty_id ??
    (typeof value?.value === "object" ? value.value?.id : value?.value) ??
    (typeof value === "string" || typeof value === "number" ? value : "")
  );

  const readScopeDirectory = async (kind: "patient" | "specialty"): Promise<any[]> => {
    const client = kind === "patient" ? PatientRelationAxiosInstance : SpecialtyAxiosInstance;
    const endpoint = kind === "patient" ? "getPatient" : "getSpecialty";
    const collected: any[] = [];
    for (let page = 1; page <= 20; page++) {
      const response = await client.get(endpoint, {
        params: { page, limit: 1000, is_deleted: "false" },
      });
      const batch = extractEntityRows(response.data);
      collected.push(...batch);
      const meta = response.data?.pagination ?? response.data?.result?.pagination ?? response.data?.meta;
      const pages = Number(meta?.totalPages);
      if (batch.length === 0 ||
        (Number.isFinite(pages) && page >= pages) ||
        (!Number.isFinite(pages) && batch.length < 1000)) break;
    }
    return collected;
  };

  const refreshScopeLinks = (linked: any[], directory: any[]): any[] => {
    const byId = new Map(directory.map(entry => [entityDirectoryKey(entry), entry]));
    return linked.map((linkedItem: any) => {
      const match = byId.get(entityDirectoryKey(linkedItem));
      if (!match) return linkedItem;
      const original = linkedItem && typeof linkedItem === "object" ? linkedItem : {};
      return {
        ...original, ...match,
        id: original.id ?? match.id ?? entityDirectoryKey(linkedItem),
        is_active: match.is_active ?? match.isActive ?? match.active ?? original.is_active,
        status: match.status ?? match.Status ?? original.status,
        __inactive: entityInactive(match),
      };
    });
  };

  const refreshScopeRecord = async (record: any): Promise<any> => {
    const [patientDirectory, specialtyDirectory] = await Promise.all([
      readScopeDirectory("patient").catch(error => {
        console.warn("Patient status lookup unavailable", error); return null;
      }),
      readScopeDirectory("specialty").catch(error => {
        console.warn("Specialty status lookup unavailable", error); return null;
      }),
    ]);
    const patientLinks = Array.isArray(record?.patient) ? record.patient :
      Array.isArray(record?.patients) ? record.patients : [];
    const specialtyLinks = Array.isArray(record?.specialty) ? record.specialty : [];
    const refreshedPatients = patientDirectory ? refreshScopeLinks(patientLinks, patientDirectory) : patientLinks;
    return {
      ...record,
      patient: refreshedPatients,
      patients: refreshedPatients,
      specialty: specialtyDirectory ? refreshScopeLinks(specialtyLinks, specialtyDirectory) : specialtyLinks,
    };
  };

  const handleOpenDialog = async (item: Result) => {
    try {
      const [response, medicalResult, customResult] = await Promise.all([
        ActivityFrequencyAxiosInstance.get(`getactivityfrequency/${item.id}`),
        readGroupDirectory("medical").then(data => ({ data })).catch(error => {
          console.warn("Could not refresh Activity Medical Groups statuses", error);
          return { data: null as any[] | null };
        }),
        readGroupDirectory("custom").then(data => ({ data })).catch(error => {
          console.warn("Could not refresh Activity Custom Groups statuses", error);
          return { data: null as any[] | null };
        }),
      ]);
      const raw: any = response.data?.result ?? response.data?.data ?? response.data;
      const detail = Array.isArray(raw) ? raw.find((r: any) => String(r.id) === String(item.id)) ?? raw[0] : raw;
      const latest = detail && typeof detail === "object" ? { ...item, ...detail } : item;
      const medical = Array.isArray(latest.medical_groups) ? latest.medical_groups : [];
      const custom = Array.isArray(latest.custom_groups) ? latest.custom_groups : [];
      const updatedScope = await refreshScopeRecord(latest);
      setSelectedItem({
        ...updatedScope,
        medical_groups: medicalResult.data ? refreshLinkedGroups(medical, medicalResult.data) : medical,
        custom_groups: customResult.data ? refreshLinkedGroups(custom, customResult.data) : custom,
      });
    } catch (error: any) {
      setSelectedItem(item);
      toast({ title: "Error", description: error.response?.data?.error || error.message || "Could not refresh relation details.", variant: "destructive" });
    }
    setIsViewDialogOpen(true);
  };
const handleCloseDialog = () => { setIsViewDialogOpen(false); setSelectedItem(null); setShowAllUpdates(false); };
  const handleEditClick = (item: Result) => { handleEdit(item); setIsDialogOpen(true); };
  const handleAddClick = () => { setEditIndex(null); clear(); setIsDialogOpen(true); };

  // â”€â”€ Table columns â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const columns: ColumnDef<Result>[] = [
    {
      accessorKey: "id",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          ID {column.getIsSorted() === "asc" ? "â†‘" : column.getIsSorted() === "desc" ? "â†“" : ""}
        </Button>
      ),
    cell: ({ row }) => (
  <div className="w-full flex justify-center font-medium">{currentPage * pageSize + row.index + 1}</div>
),
    },
    {
      accessorKey: "relation_name",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Relation name {column.getIsSorted() === "asc" ? "â†‘" : column.getIsSorted() === "desc" ? "â†“" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const val = row.getValue("relation_name") as string;
        const isDeleted = (row.original as any).is_deleted;
        return (
          <div className={`w-full text-center ${isDeleted ? "line-through text-gray-400" : ""}`}>
            {Array.isArray(val) ? val.map((v) => toDisplayText(v)).join(", ") : toDisplayText(val)}
          </div>
        );
      },
    },
    {
      accessorKey: "activity_code",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Activity {column.getIsSorted() === "asc" ? "â†‘" : column.getIsSorted() === "desc" ? "â†“" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;

        const codes: any[] = Array.isArray(item.activity_code)
          ? item.activity_code
          : [];

        const rawTypes: string[] = Array.isArray((item as any).activity_type)
          ? (item as any).activity_type
          : [];

        const codesWithTypes = codes.map((code, index) => {
          const rawType = rawTypes[index] || "cpt";
          const typeKey = TYPE_MAP[rawType.toLowerCase()] ?? "CPT";

          return {
            code,
            typeKey,
          };
        });

        const custom = Array.isArray(item.custom_groups)
          ? item.custom_groups
          : [];

        const medical = Array.isArray(item.medical_groups)
          ? item.medical_groups
          : [];

        const allDisplay = [
          ...codesWithTypes.map((entry) => ({
            label: String(entry.code),
            type: entry.typeKey,
            kind: "code" as const,
            originalCode: entry,
          })),
          ...medical.map((group: any) => ({
            label: toDisplayText(group),
            group,
            inactive: entityInactive(group),
            type: "Medical",
            kind: "medical" as const,
            originalCode: null,
          })),
          ...custom.map((group: any) => ({
            label: toDisplayText(group),
            group,
            inactive: entityInactive(group),
            type: "Custom",
            kind: "custom" as const,
            originalCode: null,
          })),
        ];

        if (allDisplay.length === 0) {
          return (
            <div className="w-full text-center text-gray-400">
              
            </div>
          );
        }

        const searchTerm = filterSearch.toLowerCase().trim();
        const hasSearch = searchTerm.length >= 2;

        const matchingDisplay = hasSearch
          ? allDisplay.filter((entry) =>
              entry.label.toLowerCase().includes(searchTerm),
            )
          : [];

        const hasMatches = hasSearch && matchingDisplay.length > 0;
        const displaySource = hasMatches ? matchingDisplay : allDisplay;
        const displayedValues = displaySource.slice(0, 3);

        const remainingCount = Math.max(
          0,
          displaySource.length - displayedValues.length,
        );

        const dialogCodes = hasMatches
          ? matchingDisplay
              .filter((entry) => entry.kind === "code" && entry.originalCode)
              .map((entry) => entry.originalCode as { code: any; typeKey: string })
          : codesWithTypes;

        const dialogMedical = hasMatches
          ? matchingDisplay
              .filter((entry) => entry.kind === "medical")
              .map((entry) => (entry as any).group)
          : medical;

        const dialogCustom = hasMatches
          ? matchingDisplay
              .filter((entry) => entry.kind === "custom")
              .map((entry) => (entry as any).group)
          : custom;

        const getStyle = (
          entry: (typeof allDisplay)[number],
          isMatching: boolean,
        ) => {
          if ((entry as any).inactive) return groupStyle((entry as any).group, entry.kind === "medical" ? "medical" : "custom");
          if (isMatching) {
            return {
              bg: "#fef08a",
              border: "#facc15",
              text: "#713f12",
            };
          }

          if (entry.kind === "code") {
            return colorMap[entry.type] ?? colorMap.CPT;
          }

          if (entry.kind === "medical") {
            return {
              bg: "#f0fdf4",
              border: "#86efac",
              text: "#166534",
            };
          }

          return {
            bg: "#faf5ff",
            border: "#d8b4fe",
            text: "#6b21a8",
          };
        };

        return (
          <div className="w-full px-2 py-1">
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 4,
                justifyContent: "center",
                marginBottom: 4,
              }}
            >
              {displayedValues.map((entry, index) => {
                const isMatching =
                  hasSearch &&
                  entry.label.toLowerCase().includes(searchTerm);

                const style = getStyle(entry, isMatching);

                return (
                  <div
                    key={`${entry.kind}-${entry.label}-${index}`}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "3px 8px",
                      borderRadius: 6,
                      border: `1px solid ${style.border}`,
                      background: style.bg,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: style.text,
                      }}
                    >
                      {entry.label}
                    </span>

                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: style.text,
                        opacity: 0.7,
                        textTransform: "uppercase" as const,
                      }}
                    >
                      {entry.type}{(entry as any).inactive ? " · Inactive" : ""}
                    </span>
                  </div>
                );
              })}
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
              }}
            >
              <span style={{ fontSize: 11, color: "#64748b" }}>
                <strong>
                  {hasMatches
                    ? `${matchingDisplay.length}/${allDisplay.length}`
                    : allDisplay.length}
                </strong>
              </span>

              {remainingCount > 0 && (
                <span
                  style={{
                    fontSize: 11,
                    color: hasMatches ? "#ca8a04" : "#3b82f6",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                  onClick={(event) => {
                    event.stopPropagation();

                    setAllCodesDialogItems(dialogCodes);

                    setAllCodesDialogRow({
                      ...row.original,
                      medical_groups: dialogMedical,
                      custom_groups: dialogCustom,
                    } as Result);

                    setAllCodesDialogOpen(true);
                  }}
                >
                  +{remainingCount} more â†’
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "is_active",
      enableSorting: false,
      header: () => <div className="w-full text-center font-normal">Status</div>,
      cell: ({ row }) => {
        const item = row.original;
        const isDeleted = (item as any).is_deleted;
        return (
          <div className="w-full flex flex-col items-center justify-center gap-1">
            <button
              onClick={() => !isDeleted && toggleActiveStatus(item)}
              disabled={!!isDeleted}
              title={isDeleted ? "Deleted relation" : undefined}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                ${isDeleted ? "opacity-40 cursor-not-allowed bg-gray-200"
                  : item.is_active ? "bg-green-500" : "bg-gray-300"}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                ${item.is_active && !isDeleted ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>
        );
      },
    },
    {
      id: "view",
      header: () => <div className="w-full text-center">View</div>,
      cell: ({ row }) => (
        <div className="w-full flex justify-center py-2">
          <button onClick={(e) => { e.stopPropagation(); handleOpenDialog(row.original); }} className="text-blue-600 hover:text-blue-800" title="View details">
            <i className="fa-solid fa-eye text-lg" />
          </button>
        </div>
      ),
    },
  ];

  // â”€â”€ Render â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const isUnifiedSearchActive = debouncedFilterSearch.trim().length >= 2;

  return (
    <div className="h5-container mx-auto px-1 pt-16 pb-4" data-qa-page="activity-frequency">
      <div className="flex justify-between items-center">
        <h5 className="mb-6 flex items-center gap-2 font-bold text-2xl mt-4 text-blue-900">
          <span className="icon-container flex items-center justify-center w-10 h-10 rounded-full bg-blue-900 text-white">
            <i className="fa-solid fa-calendar-alt" />
          </span>
          Activity frequency
        </h5>
      </div>

      {/* â”€â”€ View details dialog â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <Dialog open={isViewDialogOpen} onOpenChange={(open) => { if (!open) handleCloseDialog(); }}>
        <DialogContent className="max-w-[960px] max-h-[92vh] flex flex-col p-0 overflow-hidden bg-white border-none rounded-2xl" style={{ boxShadow: "0 25px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)" }}>
          {selectedItem && (() => {
            const codes: any[] = selectedItem.activity_code || [];
            const rawActivityTypes: string[] = Array.isArray((selectedItem as any).activity_type) ? (selectedItem as any).activity_type : [];
            const grouped: Record<string, string[]> = { CPT: [], HCPCS: [], Dental: [], Drugs: [] };
            codes.forEach((code: any, idx: number) => {
              const key = TYPE_MAP[(rawActivityTypes[idx] || "cpt").toLowerCase()] ?? "CPT";
              grouped[key].push(code);
            });
            const typeColors: Record<string, { bg: string; color: string; border: string; dot: string }> = {
              CPT:    { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe", dot: "#3b82f6" },
              HCPCS:  { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0", dot: "#22c55e" },
              Dental: { bg: "#faf5ff", color: "#7e22ce", border: "#e9d5ff", dot: "#a855f7" },
              Drugs:  { bg: "#fff7ed", color: "#c2410c", border: "#fed7aa", dot: "#f97316" },
            };
            const totalCodes = codes.length + (selectedItem.medical_groups?.length || 0) + (selectedItem.custom_groups?.length || 0);
            const viewNetworks = (selectedItem as any).network || selectedItem.networks || [];
            const viewPatients = (selectedItem as any).patient || selectedItem.patients || [];

            return (
              <>
                <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%)", padding: "24px 28px 20px", flexShrink: 0, position: "relative", overflow: "hidden" }}>
                  <div style={{ position: "absolute", top: -40, right: -40, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
                  <div style={{ position: "absolute", bottom: -30, left: -20, width: 100, height: 100, borderRadius: "50%", background: "rgba(255,255,255,0.03)" }} />
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 16, position: "relative", zIndex: 1 }}>
                    <div style={{ width: 48, height: 48, borderRadius: 14, background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, color: "#fff", flexShrink: 0, border: "1px solid rgba(255,255,255,0.1)" }}>
                      <i className="fa-solid fa-calendar-alt" />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 2 }}>
                        <h2 style={{ fontSize: 20, fontWeight: 700, color: "#fff", margin: 0, letterSpacing: "-0.01em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{toDisplayText(selectedItem.relation_name)}</h2>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 12px", borderRadius: 9999, fontSize: 11, fontWeight: 600, flexShrink: 0, background: selectedItem.is_active ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.15)", color: selectedItem.is_active ? "#86efac" : "#fca5a5", border: `1px solid ${selectedItem.is_active ? "rgba(34,197,94,0.25)" : "rgba(239,68,68,0.2)"}` }}>
                          <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} />
                          {selectedItem.is_active ? "Active" : "Inactive"}
                        </span>
                      </div>
                      <p style={{ fontSize: 12, color: "rgba(147,197,253,0.8)", margin: 0 }}>Rule ID: <span style={{ fontWeight: 600, color: "#93c5fd" }}>#{selectedItem.id}</span></p>
                    </div>
                  </div>
                </div>

                <div style={{ overflowY: "auto", flex: 1, background: "#f8fafc", padding: "6px 0 16px" }}>
                  <div style={{ padding: "20px 28px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                      <div style={{ width: 28, height: 28, borderRadius: 8, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #dbeafe" }}>
                        <i className="fa-solid fa-code-branch" style={{ fontSize: 12, color: "#3b82f6" }} />
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Activity & Frequency Configuration</span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                      <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: "16px 18px", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 14, paddingBottom: 12, borderBottom: "1px solid #f1f5f9" }}>
                          <div style={{ width: 24, height: 24, borderRadius: 6, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <i className="fa-solid fa-code" style={{ fontSize: 10, color: "#3b82f6" }} />
                          </div>
                          <span style={{ fontSize: 12, fontWeight: 700, color: "#1e293b" }}>Activity Codes</span>
                          <span style={{ marginLeft: "auto", fontSize: 11, color: "#94a3b8" }}>{totalCodes} total</span>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                          {codes.length > 0 && (
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                              {Object.entries(grouped).map(([type, codelist]) => {
                                if (codelist.length === 0) return null;
                                const tc = typeColors[type];
                                return (
                                  <div key={type} style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "12px 14px" }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                                      <span style={{ width: 6, height: 6, borderRadius: 2, background: tc.dot, flexShrink: 0 }} />
                                      <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b" }}>{type}</span>
                                      <span style={{ fontSize: 10, color: "#94a3b8", marginLeft: "auto" }}>{codelist.length}</span>
                                    </div>
                                    <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 4 }}>
                                      {codelist.map((code, idx) => (
                                        <span key={idx} style={{ padding: "2px 8px", borderRadius: 9999, fontSize: 11, fontWeight: 500, background: tc.bg, color: tc.color, border: `0.5px solid ${tc.border}` }}>{toDisplayText(code)}</span>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          {[
                            { label: "Medical Groups", items: (selectedItem.medical_groups || []), dot: "#22c55e", tagBg: "#f0fdf4", tagColor: "#15803d", tagBorder: "#bbf7d0" },
                            { label: "Custom Groups",  items: (selectedItem.custom_groups || []), dot: "#a855f7", tagBg: "#faf5ff", tagColor: "#7e22ce", tagBorder: "#e9d5ff" },
                          ].map((section) => (
                            <div key={section.label} style={{ background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0", padding: "10px 12px" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                                <span style={{ width: 6, height: 6, borderRadius: 2, background: section.dot }} />
                                <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b" }}>{section.label}</span>
                              </div>
                              <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 5 }}>
                                {section.items.length > 0
                                  ? section.items.map((item: any, i: number) => <span key={i} style={{ padding: "3px 10px", borderRadius: 9999, fontSize: 11, fontWeight: 500, background: entityInactive(item) ? "#e5e7eb" : section.tagBg, color: entityInactive(item) ? "#334155" : section.tagColor, border: `0.5px solid ${entityInactive(item) ? "#9ca3af" : section.tagBorder}` }}>{toDisplayText(item)}{entityInactive(item) ? " · Inactive" : ""}</span>)
                                  : <span style={{ color: "#94a3b8", fontSize: 12, fontStyle: "italic" }}>None</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: "16px 18px", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 14, paddingBottom: 12, borderBottom: "1px solid #f1f5f9" }}>
                          <div style={{ width: 24, height: 24, borderRadius: 6, background: "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <i className="fa-solid fa-clock" style={{ fontSize: 10, color: "#d97706" }} />
                          </div>
                          <span style={{ fontSize: 12, fontWeight: 700, color: "#1e293b" }}>Frequency Rules</span>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                          {[
                            { label: "Period Unit",      value: selectedItem.period_unit || selectedItem.frequency_type || "", icon: "fa-calendar",   accent: "#f59e0b", bg: "#fff7ed", border: "#fed7aa" },
                            { label: "Quantity",         value: String(selectedItem.quantity ?? selectedItem.frequency_limit ?? ""), icon: "fa-hashtag", accent: "#3b82f6", bg: "#eff6ff", border: "#bfdbfe" },
                            { label: "Recurring Limit",  value: String(selectedItem.recurring_limit || ""), icon: "fa-rotate",     accent: "#8b5cf6", bg: "#faf5ff", border: "#e9d5ff" },
                            { label: "Period Frequency", value: String(selectedItem.period_frq ?? selectedItem.interval ?? ""), icon: "fa-stopwatch", accent: "#10b981", bg: "#f0fdf4", border: "#bbf7d0" },
                          ].map((row) => (
                            <div key={row.label} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 10, background: row.bg, border: `1px solid ${row.border}` }}>
                              <div style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(255,255,255,0.7)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                <i className={`fa-solid ${row.icon}`} style={{ fontSize: 13, color: row.accent }} />
                              </div>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 10, fontWeight: 600, color: "#64748b", marginBottom: 2 }}>{row.label}</div>
                                <div style={{ fontSize: 15, fontWeight: 700, color: row.accent }}>{row.value}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={{ height: 1, background: "#e2e8f0", margin: "0 28px" }} />

                  <div style={{ padding: "20px 28px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                      <div style={{ width: 28, height: 28, borderRadius: 8, background: "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #fde68a" }}>
                        <i className="fa-solid fa-bullseye" style={{ fontSize: 12, color: "#d97706" }} />
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Target Scope & Visibility</span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                      {[
                        {
                          label: "Providers",
                          icon: "fa-hospital",
                          color: "#3b82f6",
                          items: (selectedItem.provider || [])
                            .map((p: any) =>
                              formatBusinessEntityLabel(
                                p,
                                [p?.Provider_id, p?.provider_id],
                                p?.Provider_name,
                                p?.provider_name,
                                p?.providerName,
                                p?.name
                              )
                            )
                            .filter(Boolean),
                          mode: (selectedItem as any).provider_mode,
                        },
                        {
                          label: "Doctors",
                          icon: "fa-stethoscope",
                          color: "#06b6d4",
                          items: (selectedItem.doctor || [])
                            .map((d: any) =>
                              formatBusinessEntityLabel(
                                d,
                                [d?.Doctor_id, d?.doctor_id],
                                d?.Doctor_name,
                                d?.doctor_name,
                                d?.doctorName,
                                d?.name
                              )
                            )
                            .filter(Boolean),
                          mode: (selectedItem as any).doctor_mode,
                        },
                        {
                          label: "Networks",
                          icon: "fa-network-wired",
                          color: "#8b5cf6",
                          items: viewNetworks
                            .map((n: any) =>
                              formatSqlEntityLabel(
                                n,
                                n?.Network_name,
                                n?.network_name,
                                n?.name
                              )
                            )
                            .filter(Boolean),
                          mode: (selectedItem as any).network_mode,
                        },
                        {
                          label: "Insurance Plans",
                          icon: "fa-shield-halved",
                          color: "#f59e0b",
                          items: (selectedItem.insuranceplan || [])
                            .map((i: any) =>
                              formatSqlEntityLabel(
                                i,
                                i?.Plan_name,
                                i?.plan_name,
                                i?.name
                              )
                            )
                            .filter(Boolean),
                          mode: (selectedItem as any).insuranceplan_mode,
                        },
                        {
                          label: "Policies",
                          icon: "fa-file-lines",
                          color: "#10b981",
                          items: (selectedItem.policy || [])
                            .map((p: any) => {
                              const planNames = (Array.isArray(p?.plans) ? p.plans : [])
                                .map(
                                  (pl: any) =>
                                    pl?.Plan_name ||
                                    pl?.plan_name ||
                                    pl?.name ||
                                    ""
                                )
                                .filter(Boolean)
                                .join(", ");

                              const base = formatBusinessEntityLabel(
                                p,
                                [p?.Policy_id, p?.policy_id],
                                p?.Policy_name,
                                p?.policy_name,
                                p?.name
                              );

                              return `${base}${planNames ? ` (${planNames})` : ""}`;
                            })
                            .filter(Boolean),
                          mode: (selectedItem as any).policy_mode,
                        },
                        {
                          label: "Patients",
                          icon: "fa-users",
                          color: "#ec4899",
                          items: viewPatients
                            .map((p: any) =>
                              formatActivityFrequencyPatientLabel(p)
                            )
                            .filter(Boolean),
                          mode: (selectedItem as any).patient_mode,
                        },
                        {
                          label: "VIP",
                          icon: "fa-star",
                          color: "#eab308",
                          items: (selectedItem.vip || [])
                            .map((v: any) =>
                              formatSqlEntityLabel(
                                v,
                                v?.name,
                                v?.vip_name,
                                v?.Vip_name
                              )
                            )
                            .filter(Boolean),
                          mode: (selectedItem as any).vip_mode,
                        },
                        {
                          label: "Payer",
                          icon: "fa-building-columns",
                          color: "#6366f1",
                          items: (selectedItem.payer || []).map((p: any) =>
                            toDisplayText(p)
                          ),
                          mode: (selectedItem as any).payer_mode,
                        },
                        {
                          label: "User Levels",
                          icon: "fa-user-gear",
                          color: "#64748b",
                          items: (selectedItem.user_level || []).map((u: any) =>
                            toDisplayText(u)
                          ),
                          mode: (selectedItem as any).user_mode,
                        },
                        {
                          label: "Specialty",
                          icon: "fa-briefcase-medical",
                          color: "#0d9488",
                          items: (selectedItem.specialty || [])
                            .map((s: any) =>
                              formatBusinessEntityLabel(
                                s,
                                [s?.speciality_code, s?.specialty_code],
                                s?.name,
                                s?.Specialty_name,
                                s?.specialty_name,
                                s?.speciality_name
                              )
                            )
                            .filter(Boolean),
                          mode: (selectedItem as any).specialty_mode,
                        },
                      ].map((e) => {
                        const isExclude = e.mode === "EXCLUDED";
                        const isDisabled = !e.mode || e.mode === "DISABLED";
                        return (
                          <div key={e.label} style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", display: "flex", flexDirection: "column" as const }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                                <div style={{ width: 24, height: 24, borderRadius: 6, background: `${e.color}12`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <i className={`fa-solid ${e.icon}`} style={{ fontSize: 10, color: e.color }} />
                                </div>
                                <span style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>{e.label}</span>
                              </div>
                              {!isDisabled && (
                                <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 9999, fontWeight: 600, background: isExclude ? "#fef2f2" : "#f0fdf4", color: isExclude ? "#dc2626" : "#15803d", border: `1px solid ${isExclude ? "#fecaca" : "#bbf7d0"}` }}>
                                  {isExclude ? "Exclusion" : "Inclusion"}
                                </span>
                              )}
                            </div>
                            {e.items.length > 0
                              ? <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 5 }}>{e.items.map((itm: string, i: number) => {
                                  const source = e.label === "Patients" ? viewPatients :
                                    e.label === "Specialty" ? (selectedItem.specialty || []) :
                                    e.label === "Policies" ? (selectedItem.policy || []) :
                                    e.label === "Doctors" ? (selectedItem.doctor || []) :
                                    e.label === "Providers" ? (selectedItem.provider || []) :
                                    e.label === "Networks" ? viewNetworks :
                                    e.label === "Insurance Plans" ? (selectedItem.insuranceplan || []) :
                                    e.label === "VIP" ? (selectedItem.vip || []) : [];
                                  const inactive = entityInactive(source[i]);
                                  return <span key={i} style={{ fontSize: 11, padding: "3px 10px", borderRadius: 9999,
                                    background: inactive ? "#e5e7eb" : "#f1f5f9",
                                    color: inactive ? "#334155" : "#334155",
                                    border: `1px solid ${inactive ? "#9ca3af" : "#e2e8f0"}`,
                                    fontWeight: inactive ? 600 : 500 }}>
                                    {itm}{inactive ? " · Inactive" : ""}
                                  </span>;
                                })}</div>
                              : <span style={{ color: "#cbd5e1", fontSize: 12, fontStyle: "italic" }}>No items</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div style={{ height: 1, background: "#e2e8f0", margin: "0 28px" }} />

                  <div style={{ padding: "20px 28px 8px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                      <div style={{ width: 28, height: 28, borderRadius: 8, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #bbf7d0" }}>
                        <i className="fa-solid fa-clock-rotate-left" style={{ fontSize: 12, color: "#16a34a" }} />
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Audit Trail</span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                        <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Created By</span>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
                          <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg, #3b82f6, #2563eb)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                            {String((selectedItem as any).created_by ?? "?")[0].toUpperCase()}
                          </div>
                          <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{(selectedItem as any).created_by != null ? resolveUserName((selectedItem as any).created_by) : ""}</span>
                        </div>
                      </div>
                      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                        <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Created On</span>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                          <i className="fa-regular fa-calendar" style={{ fontSize: 14, color: "#3b82f6" }} />
                          <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                            {(selectedItem as any).createdat || selectedItem.created_at ? formatDisplayDateTime((selectedItem as any).createdat || selectedItem.created_at, "") : ""}
                          </span>
                        </div>
                      </div>
                      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                        <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Last Updated</span>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                          <i className="fa-regular fa-clock" style={{ fontSize: 14, color: "#10b981" }} />
                          <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                            {(selectedItem as any).updatedat ? formatDisplayDateTime((selectedItem as any).updatedat) : ""}
                          </span>
                        </div>
                      </div>
                    </div>
              {Array.isArray((selectedItem as any).updated_by) && (selectedItem as any).updated_by.length > 0 && (
  <div style={{ gridColumn: "1 / -1", background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
      <i className="fa-solid fa-pen-to-square" style={{ fontSize: 11, color: "#10b981" }} />
      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.02em" }}>Update History</span>
      <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#f0fdf4", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0", marginLeft: "auto" }}>
        {(selectedItem as any).updated_by.length} update{(selectedItem as any).updated_by.length !== 1 ? "s" : ""}
      </span>
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {((selectedItem as any).updated_by as any[]).slice().reverse().slice(0, showAllUpdates ? undefined : 4).map((entry, i) => {
        const auditUserId = entry?.user_id ?? entry?.userId ?? entry?.id;
        const auditTime = entry?.timestamp ?? entry?.at ?? entry?.updated_at ?? entry?.updatedAt;
        return (
        <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, background: i === 0 ? "#f0fdf4" : "#f8fafc", border: `1px solid ${i === 0 ? "#bbf7d0" : "#e2e8f0"}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: i === 0 ? "linear-gradient(135deg, #10b981, #059669)" : "linear-gradient(135deg, #94a3b8, #64748b)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
              {String(resolveUserName(auditUserId) ?? "?")[0].toUpperCase()}
            </div>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{resolveUserName(auditUserId)}</span>
            {i === 0 && (<span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#dcfce7", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0" }}>Latest</span>)}
          </div>
          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>{formatDate(auditTime)}</span>
        </div>
        );
      })}
      {(selectedItem as any).updated_by.length > 4 && (
        <div style={{ textAlign: "center", padding: "6px 0" }}>
          <button
            onClick={() => setShowAllUpdates(v => !v)}
            style={{ fontSize: 11, color: "#3b82f6", fontWeight: 600, background: "none", border: "none", cursor: "pointer" }}
          >
            {showAllUpdates ? "Show less â†‘" : `+${(selectedItem as any).updated_by.length - 4} more updates â†“`}
          </button>
        </div>
      )}
    </div>
  </div>
)}
                  </div>
                </div>

                <div style={{ padding: "14px 28px", borderTop: "1px solid #e2e8f0", background: "#fff", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10, flexShrink: 0 }}>
                  <Button onClick={handleCloseDialog} style={{ background: "linear-gradient(135deg, #1e3a8a, #2563eb)", color: "#fff", fontWeight: 600, padding: "8px 28px", borderRadius: 10, boxShadow: "0 2px 8px rgba(37,99,235,0.3)" }}>
                    Close Details
                  </Button>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* â”€â”€ Add / Edit dialog â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl h-[92vh] flex flex-col overflow-hidden p-0">
          <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
            <DialogHeader>
              <DialogTitle style={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
                {editIndex === null ? "Add Activity Frequency" : "Edit Activity Frequency"}
              </DialogTitle>
              <DialogDescription style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>
                Define claim validation limits and recurrence rules
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-5" style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            <FormSection step={1} title="Basic Information">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div style={{ gridColumn: "1 / -1", display: "flex", flexDirection: "column", gap: 5 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>Relation name <span style={{ color: "#ef4444" }}>*</span></label>
                  <Input value={relationName} onChange={(e) => setRelationName(e.target.value)} placeholder="e.g. Physiotherapy frequency control" />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>Status</label>
                  <div style={{ display: "flex", gap: 8, marginTop: 2 }}>
                    <button type="button" onClick={() => setIsActive(true)} style={{ padding: "7px 20px", borderRadius: 8, border: "1px solid", fontSize: 13, fontWeight: 600, cursor: "pointer", borderColor: isActive ? "#16a34a" : "#d1d5db", background: isActive ? "#dcfce7" : "#f9fafb", color: isActive ? "#166534" : "#9ca3af" }}>Active</button>
                    <button type="button" onClick={() => setIsActive(false)} style={{ padding: "7px 20px", borderRadius: 8, border: "1px solid", fontSize: 13, fontWeight: 600, cursor: "pointer", borderColor: !isActive ? "#dc2626" : "#d1d5db", background: !isActive ? "#fee2e2" : "#f9fafb", color: !isActive ? "#991b1b" : "#9ca3af" }}>Inactive</button>
                  </div>
                </div>
              </div>
            </FormSection>

            <FormSection step={2} title="Activity & quantity">
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>Activity source <span style={{ color: "#ef4444" }}>*</span></label>
                  <Select value={selectedMainOption || undefined} onValueChange={(v) => setSelectedMainOption(v)}>
                    <SelectTrigger><SelectValue placeholder="Select an option" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="list_of_Activity_code">List of activity code</SelectItem>
                      <SelectItem value="medical_Activity_groups">Medical activity groups</SelectItem>
                      <SelectItem value="custom_Activity_groups">Custom activity groups</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {selectedMainOption === "list_of_Activity_code" && (
                  <div style={{ border: "2px solid #bfdbfe", borderRadius: 10, padding: 14, background: "#eff6ff" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#1d4ed8", marginBottom: 12, textTransform: "capitalize", letterSpacing: "0.02em" }}>Activity codes by type <span style={{ color: "#ef4444" }}>*</span></div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      {TYPE_COLORS.map(({ key, bg, border, text, badgeBg }) => (
                        <div key={key} style={{ background: bg, border: `1px solid ${border}`, borderRadius: 8, padding: "10px 12px", display: "flex", flexDirection: "column", height: 140, overflow: "hidden" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: text, padding: "2px 8px", borderRadius: 4, background: badgeBg, border: `1px solid ${border}` }}>{key}</span>
                            <span style={{ fontSize: 11, color: text, opacity: 0.8 }}>({activityCodeByType[key].length} code{activityCodeByType[key].length !== 1 ? "s" : ""})</span>
                          </div>
                          <div style={{ flex: 1, overflow: "hidden" }}>
                            <ListofActivityCode key={`${key}-${editIndex ?? "new"}`} onChange={(selected: Option[]) => setActivityCodeByType((prev) => ({ ...prev, [key]: selected }))} type={key} defaultValue={activityCodeByType[key]} clear={false} isValid={true} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {selectedMainOption === "medical_Activity_groups" && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>Medical activity groups <span style={{ color: "#ef4444" }}>*</span></label>
                    <ListOfActivityMedical onChange={(opts: Option[]) => setMedicalActivityGroups(opts)} defaultValue={medicalActivityGroups} clear={clearMedicalActivityGroups} />
                  </div>
                )}
                {selectedMainOption === "custom_Activity_groups" && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>Custom activity groups <span style={{ color: "#ef4444" }}>*</span></label>
                    <ListOfActivityCustom onChange={(opts: Option[]) => setCustomActivityGroups(opts)} defaultValue={customActivityGroups} clear={clearCustomActivityGroups} />
                  </div>
                )}
                <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>Quantity <span style={{ color: "#ef4444" }}>*</span></label>
                  <Input type="number" value={frequencyLimit} onChange={(e) => setFrequencyLimit(Number(e.target.value))} placeholder="0" style={{ maxWidth: 180 }} />
                </div>
              </div>
            </FormSection>

            <FormSection step={3} title="Duration & frequency">
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <PeriodCard title="Period window" hint="">
                  <div style={{ display: "flex", flexDirection: "column", width: 100 }}>
                    <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>Every <span style={{ color: "#ef4444" }}>*</span></div>
                    <Input type="number" value={interval} onChange={(e) => setInterval(Number(e.target.value))} placeholder="1" style={{ textAlign: "center", fontWeight: 700, fontSize: 15 }} />
                  </div>
                  <div style={{ width: 1, height: 36, background: "#e2e8f0", alignSelf: "flex-end" }} />
                  <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                    <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>Unit <span style={{ color: "#ef4444" }}>*</span></div>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" style={{ height: 36, fontSize: 13 }} value={frequencyType} onChange={(e) => setFrequencyType(e.target.value)}>
                      <option value="">Select unit</option>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>
                </PeriodCard>
                <PeriodCard title="Recurring limitation" required accent hint="">
                  <div style={{ display: "flex", flexDirection: "column", width: 100 }}>
                    <div style={{ fontSize: 11, color: "#1d4ed8", marginBottom: 4 }}>Limit</div>
                    <Input type="number" min={0} value={recurringLimit} onChange={(e) => setRecurringLimit(Number(e.target.value))} placeholder="0" style={{ textAlign: "center", fontWeight: 700, fontSize: 15, borderColor: "#bfdbfe" }} />
                  </div>
                  <div style={{ width: 1, height: 36, background: "#bfdbfe", alignSelf: "flex-end" }} />
                  <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                    <div style={{ fontSize: 11, color: "#1d4ed8", marginBottom: 4 }}>Unit</div>
                    <Input disabled value={frequencyType || "times"} style={{ background: "#dbeafe", color: "#1d4ed8", cursor: "default", borderColor: "#bfdbfe" }} />
                  </div>
                </PeriodCard>
              </div>
            </FormSection>

            {/* ===== ENTITIES CONFIGURATION ===== */}
            <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50 space-y-6 mb-4">
              <div className="flex items-center justify-between border-b border-blue-200 pb-4">
                <div className="flex items-center gap-2 text-blue-900">
                  <i className="fa-solid fa-layer-group text-lg"></i>
                  <h2 className="text-base font-bold tracking-tight">Entities Configuration</h2>
                </div>
                <button
                  type="button"
                  onClick={handleToggleEntities}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${showEntities ? "bg-green-500" : "bg-gray-300"}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${showEntities ? "translate-x-6" : "translate-x-1"}`} />
                </button>
              </div>

              {showEntities && (
                <div className="grid grid-cols-2 gap-4">

                  {/* Provider */}
                  <EntityCard label="Providers" icon="fa-hospital" color="blue" isActive={isProviderActive} mode={providerMode}
                    onExclusion={() => { if (providerMode === "EXCLUSION") { setProvider([]); setClearProvider(true); setSelectAllProviders(false); setProviderMode(null); setIsProviderActive(false); } else { setProvider([]); setClearProvider(true); setSelectAllProviders(false); setProviderMode("EXCLUSION"); setIsProviderActive(true); } }}
                    onInclusion={() => { if (providerMode === "INCLUSION") { setProvider([]); setClearProvider(true); setSelectAllProviders(false); setProviderMode(null); setIsProviderActive(false); } else { setProvider([]); setClearProvider(true); setSelectAllProviders(false); setProviderMode("INCLUSION"); setIsProviderActive(true); } }}
                    onClear={() => { setProvider([]); setProviderMode(null); setIsProviderActive(false); setClearProvider(true); setSelectAllProviders(false); }}>
                    <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                      <input type="checkbox" checked={selectAllProviders} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllProviders(c); if (c) { setProvider([]); setClearProvider(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-blue-600" />
                      <span className="text-sm font-medium text-slate-600">Select All Providers</span>
                    </label>
                    {!selectAllProviders && <ListOfProviderName onChange={(opts: Option[]) => setProvider(opts)} defaultValue={provider} clear={clearProvider} />}
                  </EntityCard>

                  {/* Doctor */}
                  <EntityCard label="Doctor" icon="fa-stethoscope" color="cyan" isActive={isDoctorActive} mode={doctorMode}
                    onExclusion={() => { if (doctorMode === "EXCLUSION") { setDoctor([]); setClearDoctor(true); setSelectAllDoctor(false); setDoctorMode(null); setIsDoctorActive(false); } else { setDoctor([]); setClearDoctor(true); setSelectAllDoctor(false); setDoctorMode("EXCLUSION"); setIsDoctorActive(true); } }}
                    onInclusion={() => { if (doctorMode === "INCLUSION") { setDoctor([]); setClearDoctor(true); setSelectAllDoctor(false); setDoctorMode(null); setIsDoctorActive(false); } else { setDoctor([]); setClearDoctor(true); setSelectAllDoctor(false); setDoctorMode("INCLUSION"); setIsDoctorActive(true); } }}
                    onClear={() => { setDoctor([]); setDoctorMode(null); setIsDoctorActive(false); setClearDoctor(true); setSelectAllDoctor(false); }}>
                    <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                      <input type="checkbox" checked={selectAllDoctor} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllDoctor(c); if (c) { setDoctor([]); setClearDoctor(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-cyan-600" />
                      <span className="text-sm font-medium text-slate-600">Select All Doctors</span>
                    </label>
                    {!selectAllDoctor && <ListOfDoctorName onChange={(opts: Option[]) => setDoctor(opts)} defaultValue={doctor} clear={clearDoctor} />}
                  </EntityCard>

                  {/* Networks */}
                  <EntityCard label="Networks" icon="fa-network-wired" color="purple" isActive={isNetworkActive} mode={networkMode}
                    onExclusion={() => { if (networkMode === "EXCLUSION") { setNetworks([]); setClearNetworks(true); setSelectAllNetworks(false); setNetworkMode(null); setIsNetworkActive(false); } else { setNetworks([]); setClearNetworks(true); setSelectAllNetworks(false); setNetworkMode("EXCLUSION"); setIsNetworkActive(true); } }}
                    onInclusion={() => { if (networkMode === "INCLUSION") { setNetworks([]); setClearNetworks(true); setSelectAllNetworks(false); setNetworkMode(null); setIsNetworkActive(false); } else { setNetworks([]); setClearNetworks(true); setSelectAllNetworks(false); setNetworkMode("INCLUSION"); setIsNetworkActive(true); } }}
                    onClear={() => { setNetworks([]); setNetworkMode(null); setIsNetworkActive(false); setClearNetworks(true); setSelectAllNetworks(false); }}>
                    <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                      <input type="checkbox" checked={selectAllNetworks} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllNetworks(c); if (c) { setNetworks([]); setClearNetworks(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-purple-600" />
                      <span className="text-sm font-medium text-slate-600">Select All Networks</span>
                    </label>
                    {!selectAllNetworks && <ListOfNewNetworkName onChange={(v: Option | Option[] | null) => { if (!v) { setNetworks([]); return; } setNetworks(Array.isArray(v) ? v : [v]); }} defaultValue={networks} clear={clearNetworks} />}
                  </EntityCard>

                  {/* Insurance Plan */}
                  <EntityCard label="Insurance Plan" icon="fa-shield-halved" color="amber" isActive={isInsuranceActive} mode={insuranceMode}
                    onExclusion={() => { if (insuranceMode === "EXCLUSION") { setInsuranceplan([]); setClearInsuranceplan(true); setSelectAllInsurance(false); setInsuranceMode(null); setIsInsuranceActive(false); } else { setInsuranceplan([]); setClearInsuranceplan(true); setSelectAllInsurance(false); setInsuranceMode("EXCLUSION"); setIsInsuranceActive(true); } }}
                    onInclusion={() => { if (insuranceMode === "INCLUSION") { setInsuranceplan([]); setClearInsuranceplan(true); setSelectAllInsurance(false); setInsuranceMode(null); setIsInsuranceActive(false); } else { setInsuranceplan([]); setClearInsuranceplan(true); setSelectAllInsurance(false); setInsuranceMode("INCLUSION"); setIsInsuranceActive(true); } }}
                    onClear={() => { setInsuranceplan([]); setInsuranceMode(null); setIsInsuranceActive(false); setClearInsuranceplan(true); setSelectAllInsurance(false); }}>
                    <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                      <input type="checkbox" checked={selectAllInsurance} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllInsurance(c); if (c) { setInsuranceplan([]); setClearInsuranceplan(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-amber-600" />
                      <span className="text-sm font-medium text-slate-600">Select All Insurance Plans</span>
                    </label>
                    {!selectAllInsurance && <ListOfInsuranceplanName onChange={(opts: Option[]) => setInsuranceplan(opts)} defaultValue={insuranceplan} clear={clearInsuranceplan} />}
                  </EntityCard>

                  {/* Policy */}
                  <EntityCard label="Policy" icon="fa-file-lines" color="emerald" isActive={isPolicyActive} mode={policyMode}
                    onExclusion={() => { if (policyMode === "EXCLUSION") { setPolicy([]); setClearPolicy(true); setSelectAllPolicy(false); setPolicyMode(null); setIsPolicyActive(false); } else { setPolicy([]); setClearPolicy(true); setSelectAllPolicy(false); setPolicyMode("EXCLUSION"); setIsPolicyActive(true); } }}
                    onInclusion={() => { if (policyMode === "INCLUSION") { setPolicy([]); setClearPolicy(true); setSelectAllPolicy(false); setPolicyMode(null); setIsPolicyActive(false); } else { setPolicy([]); setClearPolicy(true); setSelectAllPolicy(false); setPolicyMode("INCLUSION"); setIsPolicyActive(true); } }}
                    onClear={() => { setPolicy([]); setPolicyMode(null); setIsPolicyActive(false); setClearPolicy(true); setSelectAllPolicy(false); }}>
                    <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                      <input type="checkbox" checked={selectAllPolicy} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllPolicy(c); if (c) { setPolicy([]); setClearPolicy(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-emerald-600" />
                      <span className="text-sm font-medium text-slate-600">Select All Policies</span>
                    </label>
                    {!selectAllPolicy && <PolicyNames onChange={(v: PolicyItem[]) => setPolicy(v)} defaultValue={policy} clear={clearPolicy} />}
                  </EntityCard>

                  {/* Patients */}
                  <EntityCard label="Patients" icon="fa-users" color="pink" isActive={isPatientActive} mode={patientMode}
                    onExclusion={() => { if (patientMode === "EXCLUSION") { setPatients([]); setClearPatient(true); setSelectAllPatients(false); setPatientMode(null); setIsPatientActive(false); } else { setPatients([]); setClearPatient(true); setSelectAllPatients(false); setPatientMode("EXCLUSION"); setIsPatientActive(true); } }}
                    onInclusion={() => { if (patientMode === "INCLUSION") { setPatients([]); setClearPatient(true); setSelectAllPatients(false); setPatientMode(null); setIsPatientActive(false); } else { setPatients([]); setClearPatient(true); setSelectAllPatients(false); setPatientMode("INCLUSION"); setIsPatientActive(true); } }}
                    onClear={() => { setPatients([]); setPatientMode(null); setIsPatientActive(false); setClearPatient(true); setSelectAllPatients(false); }}>
                    <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                      <input type="checkbox" checked={selectAllPatients} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllPatients(c); if (c) { setPatients([]); setClearPatient(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-pink-600" />
                      <span className="text-sm font-medium text-slate-600">Select All Patients</span>
                    </label>
                    {!selectAllPatients && <VipPatientSelector onChange={(opts: Option[]) => setPatients(opts)} defaultValue={patients} clear={clearPatient} />}
                  </EntityCard>

                  {/* VIP */}
                  <EntityCard label="VIP" icon="fa-star" color="yellow" isActive={isVipActive} mode={vipMode}
                    onExclusion={() => { if (vipMode === "EXCLUSION") { setVip([]); setClearVip(true); setSelectAllVip(false); setVipMode(null); setIsVipActive(false); } else { setVip([]); setClearVip(true); setSelectAllVip(false); setVipMode("EXCLUSION"); setIsVipActive(true); } }}
                    onInclusion={() => { if (vipMode === "INCLUSION") { setVip([]); setClearVip(true); setSelectAllVip(false); setVipMode(null); setIsVipActive(false); } else { setVip([]); setClearVip(true); setSelectAllVip(false); setVipMode("INCLUSION"); setIsVipActive(true); } }}
                    onClear={() => { setVip([]); setVipMode(null); setIsVipActive(false); setClearVip(true); setSelectAllVip(false); }}>
                    <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                      <input type="checkbox" checked={selectAllVip} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllVip(c); if (c) { setVip([]); setClearVip(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-yellow-600" />
                      <span className="text-sm font-medium text-slate-600">Select All VIP</span>
                    </label>
                    {!selectAllVip && <Vip onChange={(opts: Option[]) => setVip(opts)} defaultValue={vip} clear={clearVip} />}
                  </EntityCard>

                  {/* Specialty */}
                  <EntityCard label="Specialty" icon="fa-briefcase-medical" color="teal" isActive={isSpecialtyActive} mode={specialtyMode}
                    onExclusion={() => { if (specialtyMode === "EXCLUSION") { setSpecialty([]); setClearSpecialty(true); setSelectAllSpecialty(false); setSpecialtyMode(null); setIsSpecialtyActive(false); } else { setSpecialty([]); setClearSpecialty(true); setSelectAllSpecialty(false); setSpecialtyMode("EXCLUSION"); setIsSpecialtyActive(true); } }}
                    onInclusion={() => { if (specialtyMode === "INCLUSION") { setSpecialty([]); setClearSpecialty(true); setSelectAllSpecialty(false); setSpecialtyMode(null); setIsSpecialtyActive(false); } else { setSpecialty([]); setClearSpecialty(true); setSelectAllSpecialty(false); setSpecialtyMode("INCLUSION"); setIsSpecialtyActive(true); } }}
                    onClear={() => { setSpecialty([]); setSpecialtyMode(null); setIsSpecialtyActive(false); setClearSpecialty(true); setSelectAllSpecialty(false); }}>
                    <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                      <input type="checkbox" checked={selectAllSpecialty} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllSpecialty(c); if (c) { setSpecialty([]); setClearSpecialty(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-teal-600" />
                      <span className="text-sm font-medium text-slate-600">Select All Specialties</span>
                    </label>
                    {!selectAllSpecialty && <ListOfSpecialty onChange={(opts: Option[]) => setSpecialty(opts)} defaultValue={specialty} clear={clearSpecialty} />}
                  </EntityCard>

                  {/* Payer */}
                  <EntityCard label="Payer" icon="fa-building-columns" color="indigo" isActive={isPayerActive} mode={payerMode}
                    onExclusion={() => { if (payerMode === "EXCLUSION") { setPayer([]); setSelectAllPayer(false); setPayerMode(null); setIsPayerActive(false); } else { setPayer([]); setSelectAllPayer(false); setPayerMode("EXCLUSION"); setIsPayerActive(true); } }}
                    onInclusion={() => { if (payerMode === "INCLUSION") { setPayer([]); setSelectAllPayer(false); setPayerMode(null); setIsPayerActive(false); } else { setPayer([]); setSelectAllPayer(false); setPayerMode("INCLUSION"); setIsPayerActive(true); } }}
                    onClear={() => { setPayer([]); setPayerMode(null); setIsPayerActive(false); setSelectAllPayer(false); }}>
                    <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                      <input type="checkbox" checked={selectAllPayer} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllPayer(c); if (c) setPayer([]); }); }} className="w-4 h-4 rounded border-slate-300 accent-indigo-600" />
                      <span className="text-sm font-medium text-slate-600">Select All Payers</span>
                    </label>
                    {!selectAllPayer && <Input value={Payer.join(",")} onChange={(e) => setPayer([e.target.value])} placeholder="Enter payer" />}
                  </EntityCard>

                  {/* User Level */}
                  <EntityCard label="User Level" icon="fa-user-gear" color="slate" isActive={isUserActive} mode={userMode}
                    onExclusion={() => { if (userMode === "EXCLUSION") { setUserLevel([]); setSelectAllUser(false); setUserMode(null); setIsUserActive(false); } else { setUserLevel([]); setSelectAllUser(false); setUserMode("EXCLUSION"); setIsUserActive(true); } }}
                    onInclusion={() => { if (userMode === "INCLUSION") { setUserLevel([]); setSelectAllUser(false); setUserMode(null); setIsUserActive(false); } else { setUserLevel([]); setSelectAllUser(false); setUserMode("INCLUSION"); setIsUserActive(true); } }}
                    onClear={() => { setUserLevel([]); setUserMode(null); setIsUserActive(false); setSelectAllUser(false); }}>
                    <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                      <input type="checkbox" checked={selectAllUser} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllUser(c); if (c) setUserLevel([]); }); }} className="w-4 h-4 rounded border-slate-300 accent-slate-600" />
                      <span className="text-sm font-medium text-slate-600">Select All User Levels</span>
                    </label>
                    {!selectAllUser && <Input value={userLevel.join(",")} onChange={(e) => setUserLevel([e.target.value])} placeholder="Enter user level" />}
                  </EntityCard>

                </div>
              )}
            </div>
            {/* ===== END ENTITIES ===== */}

          </div>

          <div className="flex-shrink-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
            <button type="button" onClick={() => { setEditIndex(null); clear(); setIsDialogOpen(false); }} style={{ padding: "8px 20px", borderRadius: 8, border: "1px solid #d1d5db", background: "#fff", color: "#6b7280", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              Cancel
            </button>
            <button type="button" onClick={() => editIndex === null ? addItem() : updateItem(editIndex)} style={{ padding: "8px 24px", borderRadius: 8, border: "none", background: "#1e40af", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", boxShadow: "0 2px 8px rgba(30,64,175,.3)" }}>
              {editIndex === null ? "Add Relation " : "Update Relation "}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* â”€â”€ All Codes Popup â”€â”€ */}
      <Dialog open={allCodesDialogOpen} onOpenChange={setAllCodesDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-blue-900">
              All Activity Codes
              <span className="ml-3 text-sm font-normal text-gray-500">
                ({allCodesDialogItems.length + (allCodesDialogRow?.custom_groups?.length ?? 0) + (allCodesDialogRow?.medical_groups?.length ?? 0)} total)
              </span>
            </DialogTitle>
          </DialogHeader>
          <div className="mt-3 overflow-y-auto max-h-[calc(80vh-120px)] flex flex-col gap-4">
            {allCodesDialogItems.length > 0 && (
              <div>
                <p style={{ fontSize: 12, fontWeight: 700, color: "#64748b", marginBottom: 8 }}>Activity Codes ({allCodesDialogItems.length})</p>
                <div className="grid grid-cols-4 gap-2">
                  {allCodesDialogItems.map((item, idx) => {
                    const c = colorMap[item.typeKey] ?? colorMap.CPT;
                    return (
                      <div key={idx} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, padding: "8px 12px", background: c.bg, border: `1px solid ${c.border}`, borderRadius: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: c.text }}>{toDisplayText(item.code)}</span>
                        <span style={{ fontSize: 10, fontWeight: 700, color: c.text, opacity: 0.7, textTransform: "uppercase" }}>{item.typeKey}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {(allCodesDialogRow?.medical_groups?.length ?? 0) > 0 && (
              <div>
                <p style={{ fontSize: 12, fontWeight: 700, color: "#64748b", marginBottom: 8 }}>Medical Groups ({allCodesDialogRow!.medical_groups.length})</p>
                <div className="grid grid-cols-4 gap-2">
                  {allCodesDialogRow!.medical_groups.map((g: any, idx: number) => (
                    <div key={idx} style={{ padding: "8px 12px", background: groupStyle(g, "medical").bg, border: `1px solid ${groupStyle(g, "medical").border}`, borderRadius: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: groupStyle(g, "medical").text }}>{toDisplayText(g)}{entityInactive(g) ? " · Inactive" : ""}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {(allCodesDialogRow?.custom_groups?.length ?? 0) > 0 && (
              <div>
                <p style={{ fontSize: 12, fontWeight: 700, color: "#64748b", marginBottom: 8 }}>Custom Groups ({allCodesDialogRow!.custom_groups.length})</p>
                <div className="grid grid-cols-4 gap-2">
                  {allCodesDialogRow!.custom_groups.map((g: any, idx: number) => (
                    <div key={idx} style={{ padding: "8px 12px", background: groupStyle(g, "custom").bg, border: `1px solid ${groupStyle(g, "custom").border}`, borderRadius: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: groupStyle(g, "custom").text }}>{toDisplayText(g)}{entityInactive(g) ? " · Inactive" : ""}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end pt-3 border-t mt-3">
            <Button onClick={() => setAllCodesDialogOpen(false)} className="bg-blue-700 hover:bg-blue-800">Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* â”€â”€ Data table â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <DataTable2
        qaIdPrefix="activity-frequency-data-table"
        columns={columns}
        data={items.result}
        validateButtonLabel="Validate"
        addButtonLabel="Add New Relation"
        showAddButton={false}
        onEdit={handleEdit}
        pageIndex={currentPage}
        onPageChange={setCurrentPage}
        manualPagination={!isUnifiedSearchActive}
        pageCount={totalPages}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageSizeChange={(nextPageSize) => {
          setPageSize(nextPageSize);
          setCurrentPage(0);
        }}
        allowedPageSizes={allowedPageSizes}
        isLoading={isTableLoading}
        onDelete={(id) => {
          const item = items.result.find(i => i.id === id);
          if ((item as any)?.is_deleted) {
            setForceId(id);
            setForceAction("hardDelete");
            setForceError("");
            setForceDialogOpen(true);
          } else {
            handleSoftDelete(id);
          }
        }}
        onRestore={canManageDeleted ? restoreItem : undefined}
        canHardDelete={canManageDeleted}
        onAdd={handleAddClick}
        onEditClick={handleEditClick}
        onDownload={handleDownload}
        isRowDeleted={(row: any) => row.is_deleted === true}
        toolbarExtras={
          <div style={{ width: "100%", position: "relative" }}>
<div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "10px 160px 10px 16px", background: "linear-gradient(135deg,#f8fafc 0%,#f1f5f9 100%)", borderRadius: 12, border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.04)", marginBottom: 12 }}>
  <ServerDeletedPills
    value={deletedFilter}
    onChange={(value) => { setDeletedFilter(value); setCurrentPage(0); }}
    />
              <button type="button" onClick={handleAddClick} style={{ display: "flex", alignItems: "center", gap: 7, padding: "8px 18px", fontSize: 13, fontWeight: 600, color: "#ffffff", background: "#1e3a8a", border: "none", borderRadius: 8, cursor: "pointer", whiteSpace: "nowrap", boxShadow: "0 2px 6px rgba(30,58,138,0.25)" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14" /></svg>
                Add New Relation
              </button>
              <div style={{ width: 1, height: 28, background: "#cbd5e1" }} />
              <UnifiedSearchBox
                value={filterSearch}
                onChange={(value) => { setFilterSearch(value); setCurrentPage(0); }}
                helpText="Relation Name, Activity Code, Medical Group, or Custom Group."
              />
              <div style={{ width: 1, height: 28, background: "#cbd5e1" }} />
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b", letterSpacing: "0.05em" }}>Status</span>
                <div style={{ display: "flex", alignItems: "center", padding: 4, background: "#e2e8f0", borderRadius: 9999 }}>
                  {[{ value: "", label: "All" }, { value: "true", label: "Active" }, { value: "false", label: "Inactive" }].map((opt) => (
                    <button key={opt.value} type="button" onClick={() => setFilterStatus(opt.value)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", fontSize: 13, fontWeight: 500, borderRadius: 9999, border: "none", cursor: "pointer", background: filterStatus === opt.value ? (opt.value === "true" ? "#10b981" : opt.value === "false" ? "#94a3b8" : "#ffffff") : "transparent", color: filterStatus === opt.value ? (opt.value === "" ? "#334155" : "#ffffff") : "#64748b", boxShadow: filterStatus === opt.value ? "0 2px 4px rgba(0,0,0,0.1)" : "none" }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ position: "absolute", top: 0, right: 16, height: 62, display: "flex", alignItems: "center" }}>
              <FileUpload fetchItems={fetchItems} />
            </div>
          </div>
        }
      />

      {/* â”€â”€â”€ Force / Hard Delete Dialog â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      {forceDialogOpen && (
        <div style={{ position: "fixed", inset: 0, background: "#00000088", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)" }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 32, width: 440, boxShadow: "0 20px 50px rgba(0,0,0,0.3)", border: "1px solid #fecaca" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 12 }}>
              <div style={{ width: 60, height: 60, borderRadius: "50%", background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className="fa-solid fa-triangle-exclamation" style={{ fontSize: 28, color: "#dc2626" }}></i>
              </div>

              {/* Hard Delete */}
              {forceAction === "hardDelete" && (
                <>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>Permanent Delete</h3>
                  <p style={{ fontSize: 13, color: "#64748b", margin: 0, lineHeight: 1.6 }}>
                    This will <strong style={{ color: "#dc2626" }}>permanently delete</strong> this activity frequency.<br />
                    This action <strong>cannot be undone</strong>.
                  </p>
                  {forceError && (
                    <div style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                        <i className="fa-solid fa-circle-info" style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}></i>
                        <p style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6 }}>{forceError}</p>
                      </div>
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
                    <button
                      onClick={() => { setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null); }}
                      style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                      Cancel
                    </button>
                    <button
                      onClick={() => forceId !== null && permanentDeleteItem(forceId, true)}
                      style={{ flex: 1, background: "#7f1d1d", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
                      <i className="fa-solid fa-bolt mr-2"></i>Force Delete Anyway
                    </button>
                  </div>
                </>
              )}

              {/* Soft Delete or Status blocked */}
              {(forceAction === "softDelete" || forceAction === "status") && (
                <>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>
                    {forceAction === "status" ? "Status Change Blocked" : "Record Is In Use"}
                  </h3>
                  <div style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                      <i className="fa-solid fa-circle-info" style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}></i>
                      <p style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6 }}>{forceError}</p>
                    </div>
                  </div>
                  <p style={{ fontSize: 13, color: "#64748b", margin: 0 }}>
                    Do you want to <strong style={{ color: "#dc2626" }}>force {forceAction === "status" ? "update status" : "delete"}</strong> anyway?
                  </p>
                  <div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
                    <button
                      onClick={() => { setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null); }}
                      style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        if (!forceId) return;
                        if (forceAction === "softDelete") forceSoftDelete(forceId);
                        else if (forceAction === "status") {
                          const item = items.result.find(i => i.id === forceId);
                          if (item) toggleActiveStatus(item, true);
                        }
                      }}
                      style={{ flex: 1, background: "#dc2626", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
                      <i className="fa-solid fa-bolt mr-2"></i>Force {forceAction === "status" ? "Update" : "Delete"}
                    </button>
                  </div>
                </>
              )}

            </div>
          </div>
        </div>
      )}

      <Toaster />
    </div>
  );
};

export default ActivityFrequency;

